// Full-screen native quote calculator (route /office/quotes/new). Opens as a
// viewport-filling tool, like the V1 standalone calculator, with a FLAT layout
// (Session ACL): every field is visible at once, no click-to-expand. Customer/site
// first, then the full lift configuration (all documentation fields shown), the
// additional items and care-home equipment, and the tools (AI site assist, scenario
// comparison, admin anchor/override/promo, templates) all laid out on the page, with
// the live Core / Care / Complete pricing in a sticky rail on the right (annual ex
// VAT, monthly inc VAT, an indicative range and the call-out rates) and Save draft +
// Send in an always-visible action bar. Only the additional items and care-home
// equipment sit behind one on-demand section, collapsed until the quote carries rows.
//
// Every price comes from the backend engine via POST /api/quotes/preview, so what
// the office sees is exactly what POST /api/quotes persists and what flows to the
// ESA. The frontend never calculates a price. Plain English throughout; the engine's
// internal "cover" key is never shown (the middle tier is always "Care").
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, Dispatch, FormEvent, SetStateAction } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { QuoteLiftInput, QuoteLineItem, QuotePreview, PostcodeLookup, AddressSuggestion, CompanySearchItem, CustomerPrefill, SitePrefill } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import {
  ADDITIONAL_ITEM_PRESETS,
  ADDITIONAL_SERVICE_DEFAULT_VISITS,
  additionalServiceFrequencyOptions,
  CARE_HOME_EQUIPMENT_PRESETS,
  CAPACITY_SUGGESTIONS,
  CONTROLLER_OPTIONS,
  DURATION_OPTIONS,
  EQUIPMENT_FREQ_LABEL,
  LIFT_TYPE_OPTIONS,
  DOOR_TYPE_OPTIONS,
  MANUFACTURER_SUGGESTIONS,
  ageBandFromYear,
  anchorFloorInfo,
  coerceFrequencyForType,
  doorTypeIsManual,
  doorTypeLabel,
  doorTypeValueFromLabel,
  DEFAULT_DOOR_TYPE,
  durationForFloors,
  durationHintForStops,
  equipmentFrequencyOptionsForMin,
  equipmentRowAnnual,
  equipmentSingleItemMinimum,
  frequencyLabel,
  frequencyOptionsForType,
  isCustomKey,
  looksLikeFullPostcode,
  multiUnitDiscountNote,
  parseManufactureYear,
  parseStops,
  perVisit,
  placeSummary,
  postcodeFromAddress,
  quoteRefFromSite,
  surchargeNoticeForPostcode,
} from "../../lib/quotePricing";
import type { EquipmentVisits, LinePreset, QuoteDuration, QuoteFrequency } from "../../lib/quotePricing";
import { QuoteTemplatesModal } from "./QuoteTemplatesModal";
import { estimateQuoteMargins } from "../../lib/quoteMargin";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/Button";
import { formatDateTime, formatTime } from "../../lib/format";
import { repairHourRate, REPAIR_EVENING_START_HOUR } from "../../lib/calloutCharge";
import {
  AUTOSAVE_DEBOUNCE_MS,
  canAutosave,
  saveAutosavePointer,
  loadAutosavePointer,
  clearAutosavePointer,
  type AutosavePointer,
} from "../../lib/quoteAutosave";

type Tier = "core" | "care" | "complete";

// Session AMP, Session RC1. The repair hourly rates as the office sees them on the tier cards, read from the client
// mirror of the fees.ts repair rule (lib/calloutCharge.ts repairHourRate, bound to the server by repairPricing.test.ts):
// GBP 125.00 on weekdays before 18:00 and GBP 250.00 on weekdays from 18:00, weekends and bank holidays, in contract or not.
const REPAIR_HOUR_DISPLAY = `£${repairHourRate(false).toFixed(2)}`;
const REPAIR_HOUR_OOH_DISPLAY = `£${repairHourRate(true).toFixed(2)}`;

// Tier display metadata. Colours are the locked brand tokens: Core green (#63C41F),
// Care purple (#7d21c7), Complete blue (#2CB9F3, the --ele-loler token).
const TIERS: { value: Tier; label: string; blurb: string; border: string; text: string; badge: string; valueNote?: string }[] = [
  { value: "core", label: "Core", blurb: "Scheduled servicing", border: "border-ele-green", text: "text-ele-green", badge: "bg-ele-green text-ele-bg" },
  { value: "care", label: "Care", blurb: "Servicing plus discounted call-outs", border: "border-ele-purple", text: "text-ele-purple", badge: "bg-ele-purple text-white" },
  { value: "complete", label: "Complete", blurb: "Servicing plus parts and out-of-hours call-outs", border: "border-ele-loler", text: "text-ele-loler", badge: "bg-ele-loler text-ele-bg", valueNote: "6 out-of-hours call-outs included per lift per year (worth up to £1,956 at standard rates)" },
];

const MAX_LIFTS = 12;
const DEFAULT_ANCHOR = "160";

interface Unit {
  id: number;
  label: string;
  liftType: string;
  customMultiplier: string;
  stops: string;
  year: string;
  duration: QuoteDuration;
  // Set true when the office manually picks a duration, cleared on any stops change.
  // NOTHING READS IT (Session AHA): the locked behaviour, auto-apply the floor-count rule on every
  // stops change and otherwise leave a manual pick alone, falls out of duration only ever being
  // written by those two handlers, so the flag is currently decorative. It is left in place rather
  // than removed because Unit is serialised into the saved quote payload via CalculatorState, so
  // dropping a field changes stored shape and is a deliberate change, not a tidy-up. Do not start
  // branching on it without re-reading the locked rule in CLAUDE.md.
  manualDurationOverride: boolean;
  // Session AIE. "The rebuild defaulted this, nobody recorded it and nobody derived it." Set only by
  // unitFromStoredLift, for a stored lift that carried no stop count / no duration, and cleared the
  // moment the office touches either field.
  //
  // They change NOTHING the office sees and NOTHING the engine prices. The form still shows the
  // makeUnit defaults (4 stops, Standard), and the server coerces an absent duration to "standard"
  // and absent stops to 4, so the quote is priced identically either way. What they change is what
  // toLiftInput PERSISTS.
  //
  // Why that is worth two booleans. At signing, allocate.ts stamps Asset.serviceDurationMult from
  // the payload's duration, so a fabricated "standard" stamps 1.00. durationForCoveredAsset then
  // treats a recorded multiplier as a deliberate manual pick and never consults the stop count
  // again, and no office screen can clear serviceDurationMult, so that lift is priced and scheduled
  // as the shortest band permanently. null is the load-bearing "nobody decided" state, and omitting
  // the key is the only way to write it. The two facts are independent (a stored lift can record one
  // and not the other), so they carry a flag each rather than sharing one.
  //
  // They ARE in inputKeyOf, and the reasoning that first left them out was wrong (adversarial
  // review, Session AIE). It said they can only change in the same edit as stops or duration, which
  // are already in the key. That is true of the FIELDS and irrelevant to the KEY, because the key is
  // a value signature rather than an edit counter: nudging the stops spinner 4 -> 5 -> 4 clears both
  // flags, so the saved body gains stops and duration, while every value in the key returns to what
  // it was and the autosave is skipped. They are never a pricing input, but they decide what is
  // persisted, and that is the test inputKeyOf's own capacity/controller note applies.
  stopsUnrecorded: boolean;
  durationUnrecorded: boolean;
  doorType: string;
  capacity: string;
  controller: string;
  manufacturer: string;
  serial: string;
  frequency: QuoteFrequency;
  // Item 9 (Session ACB): a per-lift MANUAL price override (raw input string; "" = no override).
  // Applied to the recommended tier only. Flows into the priced body via buildPricingBody.
  priceOverride: string;
}

interface LineRow {
  id: number;
  description: string;
  price: string;
  qty: string;
  // ACP care equipment model (care-home rows only; additional items never carry these).
  // minVisits is the item's LOLER minimum ("1" yearly for fixed-height items, "2"
  // six-monthly for people-lifting items); chosenVisits is the serviced cadence
  // ("1" / "2" / "4"), never below the minimum. Optional so restored pre-ACP drafts
  // (whose stored rows lack the fields) load cleanly as legacy flat rows.
  minVisits?: string;
  chosenVisits?: string;
  // Session ACR (people-lifting rows only): the last LOLER examination date, if known
  // ("yyyy-mm-dd" from the date input). Data only -- never priced; at contract activation it
  // seeds the equipment asset's examination clock (declared date + 6 months) instead of the
  // asset starting "awaiting first examination record". Optional; blank is simply unknown.
  lastExam?: string;
  // Session ACS (additional-item rows only): the tier-coverage toggle. ON = the item rides
  // the contract's tier coverage (the entered price is its CORE price; Care/Complete scale
  // proportionally on the server). OFF/absent = a flat maintenance-only line (the default,
  // and the pre-ACS behaviour for every restored draft). Equipment rows never carry it.
  tiersOn?: boolean;
  // Session ACV (covered additional-item rows on a LIFT-LESS quote only): the item's
  // service cadence ("1" yearly / "2" six-monthly / "4" quarterly; default six-monthly).
  // SCHEDULING ONLY -- it never changes the price (the entered price is the item's annual
  // Core figure at any cadence); it flows to activation, where the item materialises into
  // a scheduled asset at this frequency. The select renders only while the quote has no
  // lifts (items ride the lift visits on a mixed quote); the value is retained either way.
  serviceVisits?: string;
}

let unitSeq = 0;
let rowSeq = 0;
export function makeUnit(): Unit {
  return {
    id: unitSeq++,
    label: "",
    liftType: "passengerTrall",
    customMultiplier: "1.0",
    stops: "4",
    year: "",
    duration: "standard",
    manualDurationOverride: false,
    // A FRESH office quote records both facts. The office sees 4 stops and Standard, prices the
    // quote on them and sends it, which is a live decision and must be written. Only a rebuild from
    // a stored lift that carried neither ever sets these true.
    stopsUnrecorded: false,
    durationUnrecorded: false,
    doorType: DEFAULT_DOOR_TYPE,
    capacity: "8 person / 630kg",
    controller: "Modern microprocessor / VVVF",
    manufacturer: "",
    serial: "",
    frequency: "quarterly",
    priceOverride: "",
  };
}
function makeRow(): LineRow {
  return { id: rowSeq++, description: "", price: "", qty: "1" };
}
// A custom care-equipment row defaults to the SAFE people-lifting minimum (six-monthly):
// the office relaxes it to Yearly only by declaring the item fixed-height (spec 2.2c:
// custom rows carry a required minimum-frequency declaration -- satisfied here by a
// mandatory default rather than a silently unpriced row).
function makeEquipmentRow(): LineRow {
  return { ...makeRow(), minVisits: "2", chosenVisits: "2" };
}

// Shape a configured lift into the engine input. A clean year of manufacture maps to a
// priced age band and is recorded verbatim (pre-populates the asset).
function toLiftInput(u: Unit, currentYear: number): QuoteLiftInput {
  const year = parseManufactureYear(u.year);
  const band = ageBandFromYear(year, currentYear);
  const lift: QuoteLiftInput = { liftType: u.liftType, frequency: u.frequency };
  if (u.label.trim()) lift.label = u.label.trim();
  if (isCustomKey(u.liftType)) {
    const m = Number.parseFloat(u.customMultiplier);
    if (Number.isFinite(m)) lift.customMultiplier = m;
  }
  // Session AIE: duration and stops are OMITTED when this unit holds them only because the rebuild
  // defaulted them (see the flags on Unit). Both the wire type and the route schema already make
  // them optional with no default, and the engine coerces an absent duration to "standard" and
  // absent stops to 4, so the price does not move by a penny. What moves is the signing stamp:
  // Asset.serviceDurationMult lands null instead of a fabricated 1.00, which leaves the coverage
  // reprice free to consult the stop count instead of reading a decision nobody made.
  if (!u.durationUnrecorded) lift.duration = u.duration;
  if (!u.stopsUnrecorded) lift.stops = parseStops(u.stops);
  lift.manualDoors = doorTypeIsManual(u.doorType);
  lift.door = doorTypeLabel(u.doorType);
  if (band) lift.ageBand = band;
  if (year != null) lift.year = year;
  // Documentation only (not priced): capacity + controller for the spec table.
  if (u.capacity.trim()) lift.capacity = u.capacity.trim();
  if (u.controller.trim()) lift.controller = u.controller.trim();
  if (u.manufacturer.trim()) lift.manufacturer = u.manufacturer.trim();
  if (u.serial.trim()) lift.serial = u.serial.trim();
  return lift;
}

// Session APK: the EQUIPMENT-ONLY shape as the calculator sees it, from the PRICED preview (the server's
// own view of the equipment rows and the covered block) and the lifts on the form: no lift, no covered
// item, at least one equipment row. The server decides the same shape with isEquipmentOnlyQuote. One
// definition here, read by the rail, the save and the autosave.
export function isEquipmentOnlyPriced(
  total: { equipment?: { rows?: unknown[] } | null; additionalCovered?: { rows?: unknown[] } | null } | null,
  liftCount: number,
): boolean {
  if (!total || liftCount > 0) return false;
  if ((total.additionalCovered?.rows?.length ?? 0) > 0) return false;
  return (total.equipment?.rows?.length ?? 0) > 0;
}

// Session APK: the server refuses a manual price override on the equipment-only shape (quote.routes.ts
// EQUIPMENT_ONLY_OVERRIDE_MESSAGE, the same words): it forces a lift maintenance figure and this shape has
// none. The calculator says so where the override is and where the price is, blocks the save, and does
// not autosave a snapshot the server would refuse.
export const EQUIPMENT_ONLY_OVERRIDE_MESSAGE =
  "This quote is for care equipment only, so it has no lift maintenance figure for a manual price override to set. Adjust the item prices instead, or turn the override off.";

// What the action bar says when a save is refused for that reason: the press is answered where it was
// made, while the explanation above keeps its one home beside the override checkbox.
export const EQUIPMENT_ONLY_OVERRIDE_NOT_SAVED =
  "Not saved. The manual price override is on, and a care equipment only quote cannot carry one: turn it off in Pricing tools, then save again.";

// Clean line rows (additional items / care-home equipment) into priced line items.
// A row counts only when it has a description and a price greater than zero. With
// `equipment` set (care-home rows), a row's ACP frequency pair rides along when it is
// a valid combination (min 1|2, chosen 1|2|4, chosen clamped to at least the min); a
// row without a valid minimum is sent as a legacy flat line, exactly as before ACP,
// so a restored pre-ACP draft keeps pricing byte-identically. Session ACV: with
// `liftless` set (the quote has no lifts), a covered row also carries its service
// cadence (serviceVisitsYr, default six-monthly) -- scheduling data, never a price
// input; on a mixed quote it is deliberately NOT sent (items ride the lift visits).
function toLineItems(rows: LineRow[], equipment = false, liftless = false): QuoteLineItem[] {
  const out: QuoteLineItem[] = [];
  for (const r of rows) {
    const price = Number.parseFloat(r.price);
    const qty = Number.parseInt(r.qty, 10);
    if (r.description.trim() && Number.isFinite(price) && price > 0) {
      const item: QuoteLineItem = { description: r.description.trim(), price, qty: Number.isFinite(qty) && qty > 0 ? qty : 1 };
      // Session ACS: an additional-item row carries its tier-coverage flag (only when ON,
      // keeping the body minimal; absent parses as tiers-off server-side).
      if (!equipment && r.tiersOn === true) item.tiersOn = true;
      if (!equipment && r.tiersOn === true && liftless) {
        const sv = Number.parseInt(r.serviceVisits ?? "", 10);
        item.serviceVisitsYr = sv === 1 || sv === 2 || sv === 4 ? sv : ADDITIONAL_SERVICE_DEFAULT_VISITS;
      }
      if (equipment) {
        const min = Number.parseInt(r.minVisits ?? "", 10);
        if (min === 1 || min === 2) {
          const chosenRaw = Number.parseInt(r.chosenVisits ?? "", 10);
          const chosen = chosenRaw === 1 || chosenRaw === 2 || chosenRaw === 4 ? Math.max(chosenRaw, min) : min;
          item.minVisitsYr = min;
          item.chosenVisitsYr = chosen;
          // Session ACR: the declared last LOLER exam rides only on people-lifting rows
          // (fixed-height kit carries no examination clock). Data only, never priced.
          const lastExam = (r.lastExam ?? "").trim();
          if (min === 2 && lastExam) item.lastExamDate = lastExam;
        }
      }
      out.push(item);
    }
  }
  return out;
}

// Session AAQ: per-quote surcharge presentation (Director decision, default show).
type SurchargePresentation = "show" | "fold" | "waive";
const SURCHARGE_PRESENTATION_OPTIONS: { value: SurchargePresentation; label: string }[] = [
  { value: "show", label: "Show as a separate line (default)" },
  { value: "fold", label: "Fold into the maintenance price" },
  { value: "waive", label: "Waive (do not charge)" },
];

interface AdminTools {
  anchor: string;
  overrideOn: boolean;
  overrideTier: Tier;
  overrideValue: string;
  // Session AAQ: the office justification for a manual price override (required on save).
  overrideReason: string;
  // Session AAQ: how the congestion/parking surcharge is presented on this quote.
  surchargePresentation: SurchargePresentation;
  promoOn: boolean;
  promoPct: string;
  promoMonths: string;
  promoReason: string;
  // Soft floor (Session ANR, C59): confirms the office wants a naturally-computed price to stand even
  // though it falls below the contract floor, instead of the floor silently raising it. A DIFFERENT,
  // narrower control from the manual override above -- it forces nothing, it only lets the anchor's
  // own low figure through. Required (a reason) on save whenever a breach is live.
  floorOverrideOn: boolean;
  floorOverrideReason: string;
}
export const emptyAdmin: AdminTools = {
  anchor: DEFAULT_ANCHOR,
  overrideOn: false,
  overrideTier: "core",
  overrideValue: "",
  overrideReason: "",
  surchargePresentation: "show",
  promoOn: false,
  promoPct: "30",
  promoMonths: "6",
  promoReason: "Introductory offer",
  floorOverrideOn: false,
  floorOverrideReason: "",
};

// Build the priced request body shared by the live preview, the per-unit preview and
// the create call: the lifts, the site postcode and the site-level admin inputs.
// Session AHC: `identity` carries the customer / site / portfolio this quote belongs to. It is not
// priced directly -- the server uses it to resolve the customer's PORTFOLIO LIFT COUNT, which
// decides the multi-lift band. It must ride the SHARED body so the live preview resolves the same
// count the save will; without it the preview silently prices on this quote's lifts alone and the
// office is shown a figure the stored quote then contradicts.
export function buildPricingBody(units: Unit[], postcode: string, admin: AdminTools, additional: LineRow[], careHome: LineRow[], currentYear: number, recommendedTier: Tier, identity?: { customerId?: string | null; siteId?: string | null; portfolioId?: string | null }) {
  const anchor = Number.parseFloat(admin.anchor);
  const body: {
    lifts: QuoteLiftInput[];
    recommendedTier?: Tier;
    sitePostcode?: string;
    anchor?: number;
    additionalItems?: QuoteLineItem[];
    careHomeItems?: QuoteLineItem[];
    promo?: { on: boolean; pct: number; months: number; reason: string };
    override?: { on: boolean; value: number; tier: Tier; reason?: string };
    floorOverride?: { on: boolean; reason?: string };
    surchargePresentation?: SurchargePresentation;
    quoteCustomerId?: string;
    siteId?: string;
    portfolioId?: string;
  } = {
    lifts: units.map((u) => {
      const lift = toLiftInput(u, currentYear);
      // Item 9: a per-lift override rides the PRICED body (never the per-unit "priced alone" preview),
      // so the live total reflects it while each unit card still shows its own engine price alongside.
      const ov = Number.parseFloat(u.priceOverride);
      if (Number.isFinite(ov) && ov > 0) lift.priceOverride = ov;
      return lift;
    }),
  };
  // Item 9: carry the recommended tier only when a per-lift override is actually present, so a quote
  // without an override sends exactly the same body as before (the server defaults to core anyway).
  if (body.lifts.some((l) => l.priceOverride != null)) body.recommendedTier = recommendedTier;
  if (postcode.trim()) body.sitePostcode = postcode.trim();
  // Session AAQ: only send a non-default presentation (keeps the body minimal + back-compatible).
  if (admin.surchargePresentation !== "show") body.surchargePresentation = admin.surchargePresentation;
  if (Number.isFinite(anchor) && anchor > 0 && anchor !== 160) body.anchor = anchor;
  // Session ACV: on a lift-less quote a covered row carries its service cadence too.
  const additionalItems = toLineItems(additional, false, units.length === 0);
  // ACP (the ONE declared change to this frozen function): care-home rows carry the
  // equipment frequency pair into the priced body. Everything else is unchanged.
  const careHomeItems = toLineItems(careHome, true);
  if (additionalItems.length) body.additionalItems = additionalItems;
  if (careHomeItems.length) body.careHomeItems = careHomeItems;
  const overrideValue = Number.parseFloat(admin.overrideValue);
  if (admin.overrideOn && Number.isFinite(overrideValue) && overrideValue >= 0) {
    // Session AAQ: carry the reason (the server ignores it on the preview, requires it on save).
    const reason = admin.overrideReason.trim();
    body.override = { on: true, value: overrideValue, tier: admin.overrideTier, ...(reason ? { reason } : {}) };
  }
  // Soft floor (Session ANR, C59): carry the reason (the server ignores it on the preview, requires
  // it on save whenever the priced quote actually breaches the floor).
  if (admin.floorOverrideOn) {
    const floorReason = admin.floorOverrideReason.trim();
    body.floorOverride = { on: true, ...(floorReason ? { reason: floorReason } : {}) };
  }
  const pct = Number.parseFloat(admin.promoPct);
  const months = Number.parseInt(admin.promoMonths, 10);
  if (admin.promoOn && Number.isFinite(pct) && pct > 0 && Number.isFinite(months) && months >= 1) {
    body.promo = { on: true, pct, months, reason: admin.promoReason.trim() || "Introductory offer" };
  }
  // Session AHC: the identity the portfolio lift count is resolved from.
  if (identity?.customerId) body.quoteCustomerId = identity.customerId;
  if (identity?.siteId) body.siteId = identity.siteId;
  if (identity?.portfolioId) body.portfolioId = identity.portfolioId;
  return body;
}

// A stable signature of every priced input, used to tell whether the live total on
// screen still matches what the office is about to save.
export function inputKeyOf(units: Unit[], postcode: string, admin: AdminTools, additional: LineRow[], careHome: LineRow[], tier: Tier): string {
  return JSON.stringify([
    // capacity + controller are documentation-only (never priced) but they ARE part of the
    // saved body and the customer PDF spec table, so they count towards freshness/autosave
    // (Session AAV: a capacity-only edit must re-autosave).
    // Session AIE (adversarial review): the two provenance flags are in the key because they change
    // WHAT IS SAVED. This key is a value signature, not an edit counter, so "they can only change in
    // the same edit as stops or duration" does not hold: nudging the stops spinner 4 -> 5 -> 4 clears
    // both flags (a real decision, and the saved body gains stops + duration) while every other value
    // returns to what it was, leaving the signature identical and the autosave silently skipped. The
    // capacity/controller note above sets the precedent: part of the saved body means part of the key.
    units.map((u) => [u.liftType, u.customMultiplier, u.stops.trim(), u.year.trim(), u.duration, u.doorType, u.frequency, u.label.trim(), u.manufacturer.trim(), u.serial.trim(), u.capacity.trim(), u.controller.trim(), u.priceOverride.trim(), u.stopsUnrecorded, u.durationUnrecorded]),
    postcode.trim().toUpperCase(),
    // Soft floor (Session ANR, C59, the ONE declared change to this frozen function this session):
    // floorOverrideOn is part of the priced body (it changes whether a breaching lift's raw or
    // floored figure is charged), so it must be part of the freshness key. The reason is also part
    // of the saved body (recorded on the quote), so it counts too, exactly as overrideReason does.
    [admin.anchor, admin.overrideOn, admin.overrideTier, admin.overrideValue, admin.overrideReason, admin.floorOverrideOn, admin.floorOverrideReason, admin.surchargePresentation, admin.promoOn, admin.promoPct, admin.promoMonths, admin.promoReason],
    // Session ACS (the ONE declared change to this frozen function this session): the
    // tier-coverage toggle is part of the priced body, so it must be part of the key.
    // Session ACV adds the covered row's service cadence the same way (part of the saved
    // body on a lift-less quote, so it must count towards freshness/autosave).
    additional.map((r) => [r.description.trim(), r.price.trim(), r.qty.trim(), r.tiersOn === true, (r.serviceVisits ?? "").trim()]),
    // ACP (the ONE declared change to this frozen function): the equipment frequency
    // pair is part of the priced body, so it must be part of the freshness key. Session ACR
    // adds the declared last-exam date the same way (part of the saved body, so it must
    // count towards freshness/autosave even though it never changes the price).
    careHome.map((r) => [r.description.trim(), r.price.trim(), r.qty.trim(), (r.minVisits ?? "").trim(), (r.chosenVisits ?? "").trim(), (r.lastExam ?? "").trim()]),
    // Session ACJ: the recommended/selected tier is part of what the server prices when a per-lift
    // override is present (buildPricingBody sends recommendedTier only then), so it MUST be part of
    // the freshness key. Without it, priceFresh could stay true while the right rail showed prices
    // from a pricing run against a different tier.
    tier,
  ]);
}

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

// Scenario comparison (Session AAU Loop 3). A read-only office panel: for the current lift
// configuration it fetches every tier priced at each frequency (server-side, the ONE engine) and
// shows the annual totals side by side. It never mutates the bound quote (it re-prices on demand).
function ScenarioComparison({ body }: { body: ReturnType<typeof buildPricingBody> }) {
  const [rows, setRows] = useState<import("../../lib/api").ScenarioRow[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.quotes.scenario(body);
      setRows(res.rows);
      setOpen(true);
    } catch {
      setError("Could not build the comparison. Check the lift configuration.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={open || error ? "basis-full" : ""}>
      <button type="button" onClick={open ? () => setOpen(false) : run} disabled={busy || body.lifts.length === 0} className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50">
        {busy ? "Pricing..." : open ? "Hide" : rows ? "Show" : "Compare frequencies"}
      </button>
      {error && <p role="alert" className="mt-2 text-xs text-ele-danger">{error}</p>}
      {open && rows && (
        <div data-hscroll className="mt-2 overflow-x-auto rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
          <p className="mb-2 text-xs text-ele-body">Every tier priced at each service frequency, for this lift configuration. Read only.</p>
          <table className="w-full text-left text-sm">
            <thead className="font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Frequency</th>
                <th className="px-3 py-2 text-right">Visits/yr</th>
                <th className="px-3 py-2 text-right text-ele-green">Core</th>
                <th className="px-3 py-2 text-right" style={{ color: "#7d21c7" }}>Care</th>
                <th className="px-3 py-2 text-right text-ele-blue">Complete</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.frequency} className="border-t border-ele-purple-accent">
                  <td className="px-3 py-2 text-ele-strong">{r.label}</td>
                  {r.allowed && r.tiers ? (
                    <>
                      <td className="px-3 py-2 text-right text-ele-body">{r.visitsYr}</td>
                      <td className="px-3 py-2 text-right text-ele-green">{gbp(r.tiers.core)}</td>
                      <td className="px-3 py-2 text-right" style={{ color: "#7d21c7" }}>{gbp(r.tiers.care)}</td>
                      <td className="px-3 py-2 text-right text-ele-blue">{gbp(r.tiers.complete)}</td>
                    </>
                  ) : (
                    <td className="px-3 py-2 text-ele-body" colSpan={4}>Not permitted for this lift type (BS EN 81-28 / LOLER)</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-ele-body">Annual totals ex VAT. This view does not change the saved quote.</p>
        </div>
      )}
    </div>
  );
}

// Map an AI-suggested lift (Session AAU Loop 4) into a calculator Unit. Advisory: this is applied
// only when the office clicks Apply. Sensible defaults fill anything the AI did not provide; the
// duration follows the floor-count rule so a tall building is not left on Standard.
function assistLiftToUnit(l: import("../../lib/api").ConfigAssistLift): Unit {
  const base = makeUnit();
  const stops = typeof l.stops === "number" && l.stops > 0 ? String(l.stops) : base.stops;
  return {
    ...base,
    label: l.label ?? base.label,
    liftType: l.liftType || base.liftType,
    stops,
    year: typeof l.year === "number" ? String(l.year) : base.year,
    frequency: (l.frequency as QuoteFrequency) || base.frequency,
    duration: durationForFloors(Number.parseInt(stops, 10) || 0),
  };
}

// AI site-configuration assist (Session AAU Loop 4). ADVISORY: the office pastes a plain-English site
// description, the server (claude-sonnet-4-6) proposes a lift configuration, and the office reviews
// and APPLIES it. Nothing is priced or saved until Apply replaces the calculator units.
function SiteConfigAssist({ onApply }: { onApply: (units: Unit[]) => void }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [result, setResult] = useState<import("../../lib/api").ConfigAssistResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (description.trim().length < 3) { setError("Describe the site first."); return; }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.quotes.configAssist(description.trim()));
    } catch (err) {
      setError(err instanceof ApiError && err.code === "ai_not_connected" ? "The AI assistant is not connected. Enter the lifts manually." : "Could not generate a suggestion. Enter the lifts manually.");
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    if (!result || result.lifts.length === 0) return;
    onApply(result.lifts.map(assistLiftToUnit));
    setResult(null);
    setOpen(false);
  };

  return (
    <div className={open ? "basis-full" : ""}>
      <button type="button" className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong" onClick={() => setOpen((o) => !o)}>
        {open ? "Hide" : "Describe a site"}
      </button>
      {open && (
        <div className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
          <p className="mb-2 text-xs text-ele-body">Describe the site and let the assistant suggest the lift configuration. Advisory only. You review and apply it.</p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="e.g. Six-storey block with two 8-person passenger traction lifts installed in 2006, serviced quarterly."
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
          />
          {error && <p role="alert" className="mt-2 text-xs text-ele-danger">{error}</p>}
          <div className="mt-2 flex gap-2">
            <Button variant="secondary" size="sm" onClick={run} disabled={busy}>
              {busy ? "Thinking..." : "Suggest configuration"}
            </Button>
          </div>
          {result && (
            <div className="mt-3 rounded-ele border border-ele-purple-accent p-3 text-sm">
              {result.lifts.length === 0 ? (
                <p className="text-ele-body">{result.note}</p>
              ) : (
                <>
                  <p className="mb-2 text-xs text-ele-body">{result.note}</p>
                  <ul className="mb-3 space-y-1">
                    {result.lifts.map((l, i) => (
                      <li key={i} className="text-ele-strong">
                        {l.label ? `${l.label}: ` : `Lift ${i + 1}: `}{l.liftType}{l.stops ? `, ${l.stops} floors` : ""}{l.year ? `, ${l.year}` : ""}, {l.frequency}
                        {l.note ? <span className="block text-[11px] text-ele-body">{l.note}</span> : null}
                      </li>
                    ))}
                  </ul>
                  <Button variant="secondary" size="sm" onClick={apply}>
                    Apply {result.lifts.length} lift{result.lifts.length === 1 ? "" : "s"} to the calculator
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The internal cost-floor margin band for the recommended tier (seed 3.12). The
// cost-floor figure itself is computed server-side; the UI only renders the band:
// below the floor (red), within 1.3x of it (amber), otherwise comfortable (green).
function costFloorBand(annual: number, costFloor: number): { label: string; tone: string } {
  if (costFloor <= 0) return { label: "No cost floor", tone: "text-ele-body" };
  if (annual < costFloor) return { label: "Below cost floor", tone: "text-ele-danger" };
  if (annual < costFloor * 1.3) return { label: "Tight margin", tone: "text-amber-400" };
  return { label: "Margin OK", tone: "text-ele-green" };
}

// Split a free-text cc field (comma / semicolon / newline separated) into a trimmed,
// de-duplicated list of addresses. Empty entries dropped. Pure. Session AAU Loop 1.
export function parseCcInput(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[,;\n]+/)) {
    const email = part.trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
}

const emptyDetails = {
  customerName: "",
  customerEmail: "",
  // Session AAU (Loop 1): copy recipients, comma-separated. They get the information email and
  // accept link; only the primary customerEmail signs.
  ccEmails: "",
  // Companies House company details (Session AAP): auto-filled from the client-block company lookup,
  // editable, and stored on the quote (the number also lands on a linked customer).
  companyNumber: "",
  registeredAddress: "",
  siteName: "",
  siteAddress: "",
  sitePostcode: "",
  preferredCommencementDate: "",
};

// The calculator's full form state, stored verbatim on the quote so Resume reloads the
// exact configuration (a lossless round-trip). Unit/row ids are reassigned on restore, so
// they need not be stable. Tolerant of an older/partial saved state (missing fields default).
interface CalculatorState {
  units: Unit[];
  details: typeof emptyDetails;
  admin: AdminTools;
  additionalItems: LineRow[];
  careHomeItems: LineRow[];
  selectedTier: Tier;
  // Session AAP: the existing records this quote is raised against (so a resumed draft keeps its
  // links). Optional so an older saved state restores cleanly.
  linkedCustomerId?: string | null;
  linkedSiteId?: string | null;
  // Session AIX: the quote's own portfolio membership, if any. Without this, the live pricing
  // preview (buildPricingBody -> POST /api/quotes/preview) had no way to know this quote was
  // grouped, and showed the wrong, undiscounted-by-portfolio price while editing. The actual
  // save/revise route already resolved this correctly server-side via existing.portfolioId, so
  // this was a misleading display bug, not a data-integrity risk, but a real one to fix.
  linkedPortfolioId?: string | null;
}

// A basic email shape check for inline validation (the server is the authority; this only gives the
// office an immediate, clear message). Empty is treated as valid (email is optional to save a draft).
function isValidEmailShape(email: string): boolean {
  const e = email.trim();
  return e === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

// Re-key restored units/rows with fresh session-unique ids (the stored ids are from a
// previous session and would otherwise collide with newly added units/rows).
function restoreUnits(stored: Unit[] | undefined): Unit[] {
  if (!stored) return [makeUnit()];
  // An explicitly empty array is a saved zero-lift (equipment-only) quote: keep it empty.
  return stored.map((u) => ({
    ...makeUnit(),
    ...u,
    id: unitSeq++,
    // Session AIE: a calculatorState saved before these flags existed carries neither key, so state
    // the coercion rather than leaning on undefined being falsy. An older state was written BY this
    // calculator, whose units always held a real duration and stop count, so "recorded" is the
    // correct reading of it. A state saved SINCE keeps exactly what it stored, so a quote rebuilt
    // from a payload and then edited a second time does not have the fabrication put back.
    stopsUnrecorded: u.stopsUnrecorded === true,
    durationUnrecorded: u.durationUnrecorded === true,
  }));
}
function restoreRows(stored: LineRow[] | undefined): LineRow[] {
  return (stored ?? []).map((r) => ({ ...r, id: rowSeq++ }));
}

// ---- Rebuilding the form from a stored quote when it has no calculatorState (Session AHP) ----
//
// THE DEFECT THIS CLOSES. `calculatorState` is the calculator's OWN form state, saved verbatim on
// the quote so Edit/Resume reloads the exact configuration. Only the calculator writes one. A quote
// created by any other writer has none: the Guvnor MCP `create_quote` tool's commit payload carries
// no `calculatorState` key, so the create route persists `calculatorState: body.calculatorState ??
// null`. `restoreUnits(undefined)` then returns a single DEFAULT unit (passenger, 4 stops,
// quarterly), so pressing Edit on such a quote opened the real quote's reference against a fresh
// default lift and a blank customer. That is not merely a display fault: the save revises that
// quote IN PLACE, `revisedPayload` replaces `lifts` wholesale, and a draft writes NO QuoteVersion
// snapshot, so saving destroyed the real lifts irrecoverably. Autosave alone could trigger it,
// because a customer name is the only field it requires.
//
// The stored payload is the authority in every case: it always carries `lifts`, the customer and
// site blocks and the recommended tier. So when there is no calculatorState we rebuild the form
// from the payload instead of falling back to a default unit.
//
// FAITHFULNESS, and its LIMITS. `unitFromStoredLift` inverts `toLiftInput` above for every shape
// the two shipped writers actually produce: the calculator itself, and the MCP create_quote tool.
// Every documentation field (capacity, controller, door, manufacturer, serial) and every office
// tool (customMultiplier, priceOverride) round-trips, which is what the round-trip test pins.
//
// It is NOT lossless for every shape the route SCHEMA permits, and an earlier version of this
// comment wrongly claimed it was. Three stored distinctions are not preserved. None is reachable
// from a shipped writer today, all three are latent rather than live, and each is recorded here
// because a future non-calculator writer (RULE 4b makes more of them likely) would make one live:
//
//   1. `manualDoors` is NOT read. The door is rebuilt from the `door` LABEL alone, so a lift
//      carrying manualDoors:true with an absent or unrecognised label comes back as automatic and
//      loses the manual-door fee (+GBP 10 per stop per visit). `Unit` encodes both facts in one
//      doorType string and has no "manual, type unknown" option, so preserving it needs a form
//      change, not a mapping change. Guessing a specific manual door type here would fabricate a
//      customer-facing specification, which is the very thing the blank-capacity rule below avoids.
//   2. `ageBand` is NOT read. `Unit` holds only `year`, and `toLiftInput` re-derives the band from
//      it, so a stored band with no year is dropped and the re-saved quote loses its age uplift.
//   3. `manualDurationOverride` is deliberately never stored and is read by nothing (see its
//      declaration on Unit).
//
// A stored lift with no `duration` and no `stops` still comes back on the makeUnit DISPLAY default,
// which is deliberate (what the office sees is what gets priced). What it is no longer is written
// back out as if the office had chosen it. CLOSED IN SESSION AIE, and this paragraph used to read as
// an open flag: unitFromStoredLift now records "the rebuild defaulted this" on the unit
// (durationUnrecorded / stopsUnrecorded, declared on Unit above) and toLiftInput OMITS the key, so
// the re-saved payload carries no duration and allocate.ts stamps Asset.serviceDurationMult null
// instead of a fabricated 1.00. That is the trap the locked duration rule in CLAUDE.md names:
// durationForCoveredAsset treats a recorded multiplier as a deliberate manual pick and stops
// consulting the stop count, and no office screen can clear it. The price is identical either way
// (the engine coerces an absent duration to "standard" and absent stops to 4), and the first time
// the office touches either field the flag clears, so a real decision is always written.
//
// Anything else genuinely absent keeps the makeUnit default, which is also the value the engine
// coerces to (parseStops("") is 4), so what the office sees is what gets priced.
export function unitFromStoredLift(lift: QuoteLiftInput): Unit {
  // The DOCUMENTATION fields start BLANK rather than on the makeUnit default. makeUnit seeds a
  // fresh lift with "8 person / 630kg" and "Modern microprocessor / VVVF" as a typing convenience,
  // but a stored lift that records neither genuinely has neither: inheriting them here would write
  // a fabricated specification onto the quote on the next save, and onto the lift-specification
  // table of a customer-facing quotation PDF. On a platform lift that passenger capacity would
  // simply be wrong. Blank is dropped cleanly by toLiftInput, so the PDF omits the row instead.
  // The PRICED fields are the opposite case and do keep their defaults: see the note above.
  const u: Unit = { ...makeUnit(), id: unitSeq++, capacity: "", controller: "" };
  if (lift.liftType) u.liftType = lift.liftType;
  if (lift.label != null) u.label = lift.label;
  if (lift.customMultiplier != null) u.customMultiplier = String(lift.customMultiplier);
  // Session AIE: the two priced fields that keep their display default also record that they ARE the
  // default, so the next save omits them rather than fabricating a decision. The flag is exactly the
  // negation of the branch that assigns, so the two can never disagree about what was recorded.
  if (lift.stops != null) u.stops = String(lift.stops);
  else u.stopsUnrecorded = true;
  if (lift.year != null) u.year = String(lift.year);
  if (lift.duration) u.duration = lift.duration;
  else u.durationUnrecorded = true;
  // The stored lift keeps the door LABEL plus the derived manualDoors boolean, never the option
  // value the select binds to, so map back through the shared DOOR_TYPE_OPTIONS source.
  if (lift.door != null) u.doorType = doorTypeValueFromLabel(lift.door);
  if (lift.capacity != null) u.capacity = lift.capacity;
  if (lift.controller != null) u.controller = lift.controller;
  if (lift.manufacturer != null) u.manufacturer = lift.manufacturer;
  if (lift.serial != null) u.serial = lift.serial;
  if (lift.frequency) u.frequency = lift.frequency;
  if (lift.priceOverride != null) u.priceOverride = String(lift.priceOverride);
  return u;
}

// The inverse of toLineItems for one stored line item. Prices and quantities are numbers on the
// stored item and strings in the form.
function rowFromStoredItem(item: QuoteLineItem): LineRow {
  const r: LineRow = {
    ...makeRow(),
    description: item.description ?? "",
    price: item.price != null ? String(item.price) : "",
    qty: item.qty != null ? String(item.qty) : "1",
  };
  if (item.minVisitsYr != null) r.minVisits = String(item.minVisitsYr);
  if (item.chosenVisitsYr != null) r.chosenVisits = String(item.chosenVisitsYr);
  if (item.lastExamDate != null) r.lastExam = item.lastExamDate;
  if (item.tiersOn != null) r.tiersOn = item.tiersOn;
  if (item.serviceVisitsYr != null) r.serviceVisits = String(item.serviceVisitsYr);
  return r;
}

// The shape of a stored quote payload this rebuild reads. Everything is optional: the payload comes
// back from the API as unknown, and an older or partial record must not throw.
interface StoredQuotePayload {
  lifts?: QuoteLiftInput[];
  additionalItems?: QuoteLineItem[];
  careHomeItems?: QuoteLineItem[];
  recommendedTier?: Tier;
  preferredCommencementDate?: string | null;
  customer?: { id?: string | null; name?: string | null; email?: string | null; ccEmails?: string[] | string | null; companyNumber?: string | null; registeredAddress?: string | null } | null;
  site?: { id?: string | null; name?: string | null; address?: string | null; postcode?: string | null } | null;
}

// Rebuild a CalculatorState from a stored quote payload. Returns undefined when there is nothing
// usable to rebuild from, so the caller falls through to the ordinary fresh-calculator defaults.
export function calculatorStateFromStoredQuote(payload: unknown): CalculatorState | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const p = payload as StoredQuotePayload;
  if (!Array.isArray(p.lifts)) return undefined;
  // A top-level `lifts` array is NOT enough to identify a V2 payload. The Session ABJ sheet import
  // stores a migrated V1 quote's JSON VERBATIM (imports/sheetImport.ts), and that object also has a
  // top-level `lifts` array, but with entirely different keys: `type` (a label), `freq` (a number),
  // `mfr`, `age`, and `site` as a plain string rather than an object. Rebuilding one would produce a
  // form that LOOKS right (label, stops, capacity and the door labels happen to match) while every
  // lift had silently reverted to the default type and quarterly, and the customer block was blank.
  // Those quotes are `sent`, and Edit is offered on a sent quote, so this is reachable. Require the
  // V2 shape instead: every lift carries a `liftType`. This also rejects a null or non-object entry,
  // which would otherwise throw inside a render.
  const liftsAreV2 = p.lifts.every(
    (l) => !!l && typeof l === "object" && typeof (l as { liftType?: unknown }).liftType === "string" && (l as { liftType: string }).liftType !== "",
  );
  // A zero-lift payload carries no lift to identify, so use the V2 customer block, which the create
  // and revise routes always write and a V1 payload never has.
  const zeroLiftIsV2 = p.lifts.length > 0 || (typeof p.customer === "object" && p.customer !== null);
  if (!liftsAreV2 || !zeroLiftIsV2) return undefined;
  const cust = p.customer ?? {};
  const site = p.site ?? {};
  return {
    units: p.lifts.map(unitFromStoredLift),
    details: {
      ...emptyDetails,
      customerName: cust.name ?? "",
      customerEmail: cust.email ?? "",
      ccEmails: Array.isArray(cust.ccEmails) ? cust.ccEmails.join(", ") : (cust.ccEmails ?? ""),
      companyNumber: cust.companyNumber ?? "",
      registeredAddress: cust.registeredAddress ?? "",
      siteName: site.name ?? "",
      siteAddress: site.address ?? "",
      sitePostcode: site.postcode ?? "",
      preferredCommencementDate: p.preferredCommencementDate ?? "",
    },
    // The admin tools RESET rather than restore, unlike the two sibling restore paths
    // (restoreDraft and applyTemplate), which take them from a calculatorState. The stored payload
    // does carry an anchor, a promo, a whole-quote override and the surcharge presentation, but as
    // priced RESULTS rather than as the AdminTools form shape (the override alone needs a reason,
    // which the result does not carry). emptyAdmin equals the engine defaults, and no writer that
    // omits a calculatorState sets any of them, so this is currently a no-op. It would stop being
    // one if a future writer set a promo or an override, so it is stated rather than silent.
    admin: { ...emptyAdmin },
    additionalItems: (p.additionalItems ?? []).map(rowFromStoredItem),
    careHomeItems: (p.careHomeItems ?? []).map(rowFromStoredItem),
    selectedTier: p.recommendedTier ?? "core",
    // Keep the quote attached to the records it was raised against. Without these a save would
    // silently unlink the quote from its customer and site.
    linkedCustomerId: cust.id ?? null,
    linkedSiteId: site.id ?? null,
  };
}

// ---- The form the office edits a STORED quote with (Session AQD, Director decision D74) ----
//
// THE DEFECT THIS CLOSES. A quote's inputs have two homes: the payload the whole platform reads (its
// page, its PDF, the accept page, the ESA, the Quotes list) and `payload.calculatorState`, this form's
// own saved state, which exists because the payload cannot express the office's admin tools (see the
// note in calculatorStateFromStoredQuote: a rebuild RESETS the anchor, promo, override and surcharge
// presentation). One writer moves only the first of them: the office's frequency confirmation gate
// (POST /api/quotes/:id/confirm-frequency) reprices the stored definition when the customer asks for a
// different cadence at acceptance, and never touches the form state.
//
// That is invisible while the quote is accepted (it cannot be edited), and surfaces on the two routes
// that make an EDITABLE quote from it, both of which copy the definition faithfully and correctly:
// Duplicate (Session APU) and Reopen (Session APW). Edit then opened the calculator at the cadence
// originally quoted, priced it there, and the next save or autosave wrote that price back over the
// confirmed one, with nobody deciding it should.
//
// THE RULE: the stored quote wins. Its lifts are what every other surface prices from, so wherever the
// two disagree the form takes the lift's own frequency. That is RULE 6 applied to a cached copy: the
// form state stays the home of what only it holds, and stops being a second, silent home for what the
// quote already records. It is derived at every load, so it corrects every quote already on file with
// no migration, no backfill and no second writer, and it covers an Edit from any route.
//
// The Director's decision (D74, 22 September 2026) is that the form opens at the CONFIRMED cadence, and
// quietly: the office can change it like any other field, and the alternative (a copy reverting to the
// cadence first quoted) would have to reprice the copy and reverse what Session APU pinned.
//
// FREQUENCY IS THE WHOLE LIST TODAY, and that is checked rather than assumed:
// server/src/modules/quotes/confirmedFrequencyDefinition.test.ts fails, naming the key, if a reprice
// ever rewrites another engine input this form also holds. Widen this function in the same session.
export function withStoredLiftFrequencies(state: CalculatorState, payload: unknown): CalculatorState {
  const lifts = payload && typeof payload === "object" ? (payload as { lifts?: unknown }).lifts : undefined;
  if (!Array.isArray(lifts) || !Array.isArray(state.units)) return state;
  let reconciled = false;
  const units = state.units.map((u, i) => {
    const stored = lifts[i];
    const freq = stored && typeof stored === "object" ? (stored as { frequency?: unknown }).frequency : undefined;
    // Only a cadence THIS UNIT'S OWN SELECT OFFERS, which is narrower than the five the platform has: annual is
    // offered but DISABLED on every lift type that may not take it (the locked annual-frequency rule), so the
    // global list would seat the form on an option the office cannot re-pick, whose preview the server then
    // refuses, leaving the price stale and Save disabled until somebody spotted the cadence. A V1 import's lift
    // carries `freq: 4` and no `frequency` at all, and a payload written outside the quote routes (the admin
    // Master Sheet import stores one verbatim) could carry anything. Refused here, the form keeps its own
    // cadence, which is the safe direction. Found by the Session AQD review.
    const offered = typeof freq === "string" && frequencyOptionsForType(u.liftType).some((o) => o.value === freq && o.disabled !== true);
    if (!offered || freq === u.frequency) return u;
    reconciled = true;
    return { ...u, frequency: freq as QuoteFrequency };
  });
  // The same object when nothing moved, so an ordinary Edit renders and autosaves exactly as before.
  return reconciled ? { ...state, units } : state;
}

// Seed-or-rebuild, then reconcile: the quote's saved form state if it has one, else the rebuild from its
// payload (Session AHP, for a quote the MCP create_quote tool wrote), reconciled against the quote either
// way. THE RULE ITSELF IS withStoredLiftFrequencies ABOVE, and it has exactly two application points: this
// function, used by the navigation seed and the mount re-fetch, and restoreDraft, which calls the rule
// directly because it must NOT fall back to the rebuild (a draft with no form state is not restorable).
// Widen the RULE, not this wrapper. storedLiftFrequency.test.ts's "every way into the calculator goes
// through the rule" scans fail if a third application point reads a stored calculatorState without the
// rule, or if a screen navigates here without handing over the quote to reconcile against.
export function calculatorStateForEditing(payload: unknown, saved?: CalculatorState): CalculatorState | undefined {
  const state = saved ?? calculatorStateFromStoredQuote(payload);
  return state ? withStoredLiftFrequencies(state, payload) : undefined;
}

export function QuoteCalculatorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Resume: the Quotes list opens the calculator with a saved quote's form state to edit and
  // re-send (Resume a draft, or Edit and resend). `resumeId` is the quote to REVISE in place
  // (keeping its ref); when null this is a brand-new quote. `status` (Session AAV) tells the
  // autosave whether this is a DRAFT (autosave-eligible) or an already-sent quote (never
  // autosaved: a silent revise would flip it back to draft and write version snapshots).
  const navState = location.state as
    | {
        prefill?: Partial<typeof emptyDetails>;
        // Session ALO: ties a fresh quote to an EXISTING customer/site (e.g. a LOLER report already
        // linked to one), the same way a resumed quote's linkedCustomerId/linkedSiteId do. Absent for
        // a genuinely new customer, matching the prospect "Generate Quote" flow, which has never sent
        // one either.
        link?: { customerId?: string | null; siteId?: string | null };
        // Session ALO: one lift's worth of real inputs to seed the calculator with (e.g. a LOLER
        // report's own recorded specification), in the same stored-lift shape a saved quote uses.
        // unitFromStoredLift converts it into the form's Unit shape; the office still reviews every
        // field before saving.
        initialLift?: QuoteLiftInput;
        resume?: { id: string; ref?: string; status?: string; calculatorState?: CalculatorState; payload?: unknown; portfolioId?: string | null };
      }
    | null;
  // Session AHP: a quote written by anything other than this calculator carries no calculatorState
  // (the Guvnor MCP create_quote tool is the live case), so fall back to rebuilding the form from
  // the quote's OWN stored payload. Without this, Edit opened a real quote's reference against a
  // single default lift and a blank customer, and saving overwrote the real ones. Computed once
  // (this assigns fresh unit/row ids, which must not be re-issued on every render).
  const [resumed] = useState<CalculatorState | undefined>(() =>
    calculatorStateForEditing(navState?.resume?.payload, navState?.resume?.calculatorState),
  );
  const [resumeId] = useState<string | null>(navState?.resume?.id ?? null);
  // The ref of the quote being edited (Session AAG), shown as an "Editing quote [ref]" banner so
  // the office knows a save revises this quote in place rather than creating a new one.
  const [editingRef] = useState<string | null>(navState?.resume?.ref ?? null);
  const resumeStatus = navState?.resume?.status ?? null;
  // Autosave applies to a brand-new quote or a resumed DRAFT only (Session AAV, Loop 2).
  const autosaveEligible = resumeId == null || resumeStatus === "draft";
  // Session ABC Loop 3: a genuinely fresh calculator (no resume, no prospect prefill) drops
  // focus straight into the site search, the fastest prefill entry (it fills the customer too).
  const freshOpen = resumeId == null && !navState?.prefill;

  // Session ALO: a fresh "create maintenance quote" navigation (no resume) seeds ONE unit from
  // navState.initialLift via the SAME converter Edit already uses to rebuild a unit from a stored
  // lift, rather than a second mapping. restoreUnits still owns the resumed-draft path unchanged.
  const [units, setUnits] = useState<Unit[]>(() =>
    resumed?.units ? restoreUnits(resumed.units) : navState?.initialLift ? [unitFromStoredLift(navState.initialLift)] : [makeUnit()],
  );
  // Locked default recommended tier is Core.
  const [selectedTier, setSelectedTier] = useState<Tier>(resumed?.selectedTier ?? "core");
  // Resume restores the saved details; otherwise an optional prefill from a "Generate quote"
  // action (e.g. the prospect detail page) seeds the customer/site so the office never re-keys.
  const [details, setDetails] = useState(() =>
    resumed?.details ? { ...emptyDetails, ...resumed.details } : { ...emptyDetails, ...(navState?.prefill ?? {}) },
  );
  // Session AAP: the existing customer/site this quote is linked to (set by picking from the
  // autocomplete of existing records). Sent as customerId/siteId so the quote links and the Companies
  // House number lands on the linked customer. Restored from a resumed draft, or (Session ALO) from
  // navState.link when a fresh quote is already tied to a real customer/site.
  const [linkedCustomerId, setLinkedCustomerId] = useState<string | null>(resumed?.linkedCustomerId ?? navState?.link?.customerId ?? null);
  const [linkedSiteId, setLinkedSiteId] = useState<string | null>(resumed?.linkedSiteId ?? navState?.link?.siteId ?? null);
  // Session AIX: prefer the fresh portfolioId QuotesPage.tsx now sends straight off the DB record
  // (navState.resume.portfolioId) over any stale value a previously-saved calculatorState carries,
  // since the database is authoritative and a quote's portfolio membership can change after a save.
  const [linkedPortfolioId, setLinkedPortfolioId] = useState<string | null>(
    navState?.resume?.portfolioId ?? resumed?.linkedPortfolioId ?? null,
  );
  const [admin, setAdmin] = useState<AdminTools>(() => (resumed?.admin ? { ...emptyAdmin, ...resumed.admin } : { ...emptyAdmin }));
  const [additionalItems, setAdditionalItems] = useState<LineRow[]>(() => restoreRows(resumed?.additionalItems));
  const [careHomeItems, setCareHomeItems] = useState<LineRow[]>(() => restoreRows(resumed?.careHomeItems));
  // Session AAT Loop 4: reusable quote templates (config only, no customer/site identity).
  // Session AAV: the command palette's "New quote from template" opens the calculator with
  // ?templates=1, which opens the picker straight away.
  const [templatesOpen, setTemplatesOpen] = useState(() => new URLSearchParams(location.search).get("templates") === "1");
  // Session AAT Loop 5: the loaded labour cost per hour for the OFFICE-ONLY margin display (never
  // shown to the customer, never stored on the quote). Fetched from tenant settings (default 62).
  const [labourCostPerHour, setLabourCostPerHour] = useState<number | null>(null);
  const [showWorkings, setShowWorkings] = useState(false);
  // ---- Extras disclosure (Session ACL: the one remaining on-demand section) ----
  // The calculator is flat, V1 style: customer details, lift details, admin tools and templates
  // are all visible at once. Only the additional items and care-home equipment stay behind one
  // disclosure, collapsed unless the quote already carries rows. This flag is never saved on the
  // quote (calculatorState is unchanged); it only shapes the view.
  const [extrasOpen, setExtrasOpen] = useState(
    () => (resumed?.additionalItems?.length ?? 0) + (resumed?.careHomeItems?.length ?? 0) > 0,
  );
  // Session ABC Loop 3: once an existing site is picked (prefill filled everything), the
  // customer & site fields collapse to a compact summary with one Change control. Manual
  // Change re-expands; picking a site again re-collapses. UI-only, never saved.
  const [whoForExpanded, setWhoForExpanded] = useState(false);

  const [total, setTotal] = useState<QuotePreview | null>(null);
  const [unitPrices, setUnitPrices] = useState<Record<number, QuotePreview | "error">>({});
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [pricedKey, setPricedKey] = useState<string | null>(null);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ---- Autosave (Session AAV, Loop 2) ----
  // The id of the draft the autosave created (a new quote's first autosave allocates it); an
  // explicit Save/Send then revises the SAME draft, so no duplicate quote is ever created.
  // A REF (not state): submit must read the LATEST id after awaiting an in-flight autosave,
  // never a stale render closure, and nothing renders from it.
  const autosaveIdRef = useRef<string | null>(null);
  // The quiet footer status: the autosaved draft's ref + when it last saved.
  const [autosaveStatus, setAutosaveStatus] = useState<{ ref: string; at: Date } | null>(null);
  // A restorable draft found on return (the localStorage pointer, verified against the server
  // before offering). Dismissed by Restore, Start fresh, or the first autosave of new work.
  const [restoreOffer, setRestoreOffer] = useState<AutosavePointer | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  // Serialise autosaves: never two in flight, and never one while an explicit save runs.
  const autosaveInFlight = useRef(false);
  // The in-flight autosave request, so an explicit save can AWAIT it (and then revise the
  // draft it created) instead of racing it into a duplicate quote.
  const autosavePromise = useRef<Promise<void> | null>(null);
  // Once an explicit save/send begins (or the server reports the draft is no longer a draft),
  // autosaving stops for good: no late pointer write can resurrect a stale restore offer.
  const autosaveStopped = useRef(false);
  // The signature of the last autosaved state, so an unchanged form never re-saves; the
  // INITIAL signature (a resumed draft's stored state) also never re-saves, so merely opening
  // a draft to look at it writes nothing.
  const lastAutosavedSig = useRef<string | null>(null);
  const initialSigRef = useRef<string | null>(null);
  // Bumped when an autosave settles, re-arming the debounce for a snapshot whose timer fired
  // while a previous save was still in flight (that snapshot would otherwise be dropped).
  const [autosaveTick, setAutosaveTick] = useState(0);

  const inputKey = useMemo(() => inputKeyOf(units, details.sitePostcode, admin, additionalItems, careHomeItems, selectedTier), [units, details.sitePostcode, admin, additionalItems, careHomeItems, selectedTier]);

  // Live surcharge notice for the postcode field (Session AAE): computed per keystroke
  // from the client mirror of the server zone lists (parity-tested), so the office sees
  // the surcharge immediately; the debounced preview then prices it authoritatively.
  const postcodeNotice = useMemo(() => surchargeNoticeForPostcode(details.sitePostcode), [details.sitePostcode]);

  // Session AAG: when the office types or pastes a site address whose tail is a postcode but leaves
  // the dedicated postcode field blank, auto-fill it from the address (visible and editable) so the
  // surcharge notice fires and the priced request carries the postcode. Only fills an EMPTY field,
  // never overwriting a manual entry; keyed on the address so clearing the postcode is respected.
  useEffect(() => {
    const derived = postcodeFromAddress(details.siteAddress);
    if (!derived) return;
    setDetails((d) => (d.sitePostcode.trim() ? d : { ...d, sitePostcode: derived }));
  }, [details.siteAddress]);

  // Session AAP: free postcode validation + geography fill (postcodes.io via the server seam). When a
  // full postcode is typed, debounce a lookup that fills town/district/region + drops lat/lng (cached
  // server-side). Best-effort: an unresolved/failed lookup leaves manual entry intact -- the surcharge
  // notice above already fires from the client mirror, independent of this lookup. resolvedPlace holds
  // the last confirmed geography; postcodeUnknown flags a full-looking postcode the provider rejected.
  const [resolvedPlace, setResolvedPlace] = useState<PostcodeLookup | null>(null);
  const [postcodeChecking, setPostcodeChecking] = useState(false);
  const [postcodeUnknown, setPostcodeUnknown] = useState(false);
  useEffect(() => {
    const pc = details.sitePostcode.trim();
    if (!looksLikeFullPostcode(pc)) {
      setResolvedPlace(null);
      setPostcodeUnknown(false);
      setPostcodeChecking(false);
      return;
    }
    let cancelled = false;
    setPostcodeChecking(true);
    setPostcodeUnknown(false);
    const t = setTimeout(() => {
      api.address
        .postcode(pc)
        .then((res) => {
          if (cancelled) return;
          if (res.ok && res.result) {
            setResolvedPlace(res.result);
            setPostcodeUnknown(false);
          } else {
            setResolvedPlace(null);
            setPostcodeUnknown(true);
          }
        })
        .catch(() => {
          // Silent fallback: a provider/network failure must never block the form.
          if (!cancelled) {
            setResolvedPlace(null);
            setPostcodeUnknown(false);
          }
        })
        .finally(() => {
          if (!cancelled) setPostcodeChecking(false);
        });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [details.sitePostcode]);

  // Session AAP: free-text site-address autocomplete (Nominatim via the server seam). While the
  // address field is focused and holds a few characters, debounce a search and offer suggestions;
  // picking one fills the address (and its postcode when present). Manual typing always works; a
  // failed search shows no suggestions and never blocks entry. Gated on focus so it never fires from
  // a programmatic fill (Resume / prefill / postcode-from-address).
  const [addrFocused, setAddrFocused] = useState(false);
  const [addrSuggestions, setAddrSuggestions] = useState<AddressSuggestion[]>([]);
  const [addrSearching, setAddrSearching] = useState(false);
  // The label just picked from the dropdown. The search effect skips a query equal to it, so picking a
  // suggestion does not immediately re-search the value it just filled in (which would reopen the
  // dropdown). Editing the address away from the picked value re-enables the search.
  const lastPickedAddress = useRef<string | null>(null);
  useEffect(() => {
    if (!addrFocused) return;
    const q = details.siteAddress.trim();
    if (q.length < 4 || q === lastPickedAddress.current) {
      setAddrSuggestions([]);
      setAddrSearching(false);
      return;
    }
    let cancelled = false;
    setAddrSearching(true);
    const t = setTimeout(() => {
      api.address
        .search(q)
        .then((res) => {
          if (!cancelled) setAddrSuggestions(res.results);
        })
        .catch(() => {
          if (!cancelled) setAddrSuggestions([]);
        })
        .finally(() => {
          if (!cancelled) setAddrSearching(false);
        });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [details.siteAddress, addrFocused]);

  // Pick a Nominatim suggestion: fill the address, and its postcode when the match carries one. The
  // lastPickedAddress ref stops the search effect from re-firing on the value it just filled in, so
  // the dropdown closes and stays closed until the office edits the address again.
  function pickAddressSuggestion(s: AddressSuggestion) {
    lastPickedAddress.current = s.label;
    setDetails((d) => ({ ...d, siteAddress: s.label, sitePostcode: s.postcode ?? d.sitePostcode }));
    setAddrSuggestions([]);
  }

  // Session AAP: Companies House company lookup in the CLIENT block. Search a name or number; picking
  // a result fills the customer name, company number and registered office address (the client's own
  // legal details, distinct from the site address which is never touched here). Degrades to manual
  // entry when the key is unset (companyConfigured false) or on any failure. companyConfigured starts
  // unknown (null) and is set from the first search response.
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyResults, setCompanyResults] = useState<CompanySearchItem[]>([]);
  const [companySearching, setCompanySearching] = useState(false);
  const [companyConfigured, setCompanyConfigured] = useState<boolean | null>(null);
  const [companyFocused, setCompanyFocused] = useState(false);
  const [companyPicking, setCompanyPicking] = useState(false);
  useEffect(() => {
    if (!companyFocused) return;
    const q = companyQuery.trim();
    if (q.length < 2) {
      setCompanyResults([]);
      setCompanySearching(false);
      return;
    }
    let cancelled = false;
    setCompanySearching(true);
    const t = setTimeout(() => {
      api.companies
        .search(q)
        .then((res) => {
          if (cancelled) return;
          setCompanyConfigured(res.configured);
          setCompanyResults(res.results);
        })
        .catch(() => {
          if (!cancelled) setCompanyResults([]);
        })
        .finally(() => {
          if (!cancelled) setCompanySearching(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [companyQuery, companyFocused]);

  // Pick a company: resolve its full profile (registered office) and fill the client block. Falls back
  // to the search item's own fields if the profile fetch fails, so a pick never silently does nothing.
  async function pickCompany(item: CompanySearchItem) {
    setCompanyPicking(true);
    setCompanyResults([]);
    // Clearing the query hides the dropdown (too short to search) while keeping the field usable, so a
    // follow-up search still works without a blur/refocus.
    setCompanyQuery("");
    try {
      const profile = await api.companies.get(item.companyNumber).catch(() => null);
      setDetails((d) => ({
        ...d,
        customerName: profile?.companyName ?? item.title,
        companyNumber: profile?.companyNumber ?? item.companyNumber,
        registeredAddress: profile?.registeredOfficeAddress ?? item.addressSnippet ?? d.registeredAddress,
      }));
    } finally {
      setCompanyPicking(false);
    }
  }

  // Session AAP (Loop 3): raise a quote against an EXISTING customer or site. The customer-name and
  // site-name fields double as search boxes over existing records; picking one pre-fills every known
  // field and links the quote (customerId / siteId). A lastPicked ref stops the just-picked value from
  // re-searching. Editing a linked field by hand clears that link (the value no longer matches).
  const [customerMatches, setCustomerMatches] = useState<CustomerPrefill[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerFocused, setCustomerFocused] = useState(false);
  const lastPickedCustomerName = useRef<string | null>(null);
  useEffect(() => {
    if (!customerFocused) return;
    const q = details.customerName.trim();
    if (q.length < 2 || q === lastPickedCustomerName.current) {
      setCustomerMatches([]);
      setCustomerSearching(false);
      return;
    }
    let cancelled = false;
    setCustomerSearching(true);
    const t = setTimeout(() => {
      api.quotePrefill
        .customers(q)
        .then((rows) => {
          if (!cancelled) setCustomerMatches(rows);
        })
        .catch(() => {
          if (!cancelled) setCustomerMatches([]);
        })
        .finally(() => {
          if (!cancelled) setCustomerSearching(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [details.customerName, customerFocused]);

  const [siteMatches, setSiteMatches] = useState<SitePrefill[]>([]);
  const [siteSearching, setSiteSearching] = useState(false);
  const [siteNameFocused, setSiteNameFocused] = useState(false);
  const lastPickedSiteName = useRef<string | null>(null);
  useEffect(() => {
    if (!siteNameFocused) return;
    const q = details.siteName.trim();
    if (q.length < 2 || q === lastPickedSiteName.current) {
      setSiteMatches([]);
      setSiteSearching(false);
      return;
    }
    let cancelled = false;
    setSiteSearching(true);
    const t = setTimeout(() => {
      api.quotePrefill
        .sites(q)
        .then((rows) => {
          if (!cancelled) setSiteMatches(rows);
        })
        .catch(() => {
          if (!cancelled) setSiteMatches([]);
        })
        .finally(() => {
          if (!cancelled) setSiteSearching(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [details.siteName, siteNameFocused]);

  // Typing in the customer/site name field is a manual edit: clear the matching link (the typed value
  // no longer identifies the linked record). A pick fills via setDetails (no onChange), so it keeps its
  // link. Reset the lastPicked guard so the edited value searches.
  function onCustomerNameChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setDetails((d) => ({ ...d, customerName: value }));
    lastPickedCustomerName.current = null;
    if (linkedCustomerId) setLinkedCustomerId(null);
    // Session AIX: a portfolio belongs to one customer, so unlinking the customer invalidates any
    // portfolio membership the live preview was pricing against.
    if (linkedPortfolioId) setLinkedPortfolioId(null);
  }
  function onSiteNameChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setDetails((d) => ({ ...d, siteName: value }));
    lastPickedSiteName.current = null;
    if (linkedSiteId) setLinkedSiteId(null);
  }

  // Pick an existing customer: fill the client fields and link. Choosing a customer unlinks any site
  // (the site's customer might differ; the office can re-pick a site under this customer).
  function pickCustomer(id: string) {
    const c = customerMatches.find((x) => x.id === id);
    if (!c) return;
    lastPickedCustomerName.current = c.name;
    setDetails((d) => ({ ...d, customerName: c.name, customerEmail: c.email ?? d.customerEmail, companyNumber: c.companyNumber ?? d.companyNumber }));
    setLinkedCustomerId(c.id);
    setLinkedSiteId(null);
    // Session AIX: a freshly-picked customer's portfolio membership is unknown here (a new pick is
    // not necessarily the same customer this quote was previously linked to); reset it rather than
    // carry a stale portfolio id forward into a possibly-unrelated customer.
    setLinkedPortfolioId(null);
    // Clearing the matches (with lastPicked set above) hides the dropdown while keeping the field
    // focused + usable, so a follow-up edit re-searches without a blur/refocus.
    setCustomerMatches([]);
  }

  // Pick an existing site: fill the site AND its customer's fields, and link both.
  function pickSite(id: string) {
    const s = siteMatches.find((x) => x.id === id);
    if (!s) return;
    lastPickedSiteName.current = s.name;
    lastPickedCustomerName.current = s.customerName;
    setDetails((d) => ({
      ...d,
      siteName: s.name,
      siteAddress: s.address,
      sitePostcode: s.postcode ?? d.sitePostcode,
      customerName: s.customerName,
      customerEmail: s.customerEmail ?? d.customerEmail,
      companyNumber: s.customerCompanyNumber ?? d.companyNumber,
    }));
    setLinkedSiteId(s.id);
    setLinkedCustomerId(s.customerId);
    setSiteMatches([]);
    // The prefill has done the work: collapse the customer & site fields to the summary.
    setWhoForExpanded(false);
  }

  const emailInvalid = !isValidEmailShape(details.customerEmail);

  function close() {
    navigate("/office/quotes");
  }

  // Live re-pricing (debounced) whenever any priced input changes. The combined call
  // is the authoritative total (postcode surcharge, extras, promo and override
  // included). Per-unit prices price each lift on its own at the current anchor.
  useEffect(() => {
    let cancelled = false;
    setPreviewing(true);
    setPreviewError(false);
    const t = setTimeout(() => {
      const currentYear = new Date().getFullYear();
      const ids = units.map((u) => u.id);
      const body = buildPricingBody(units, details.sitePostcode, admin, additionalItems, careHomeItems, currentYear, selectedTier, { customerId: linkedCustomerId, siteId: linkedSiteId, portfolioId: linkedPortfolioId });
      const keyNow = inputKeyOf(units, details.sitePostcode, admin, additionalItems, careHomeItems, selectedTier);
      const anchor = body.anchor;
      const totalReq = api.quotes.preview(body);
      // Per-unit breakdown: each unit priced alone (no surcharge/extras), at the anchor.
      const perUnitReq: Promise<(QuotePreview | "error")[]> = Promise.all(
        units.map((u) =>
          api.quotes
            .preview({ lifts: [toLiftInput(u, currentYear)], ...(anchor ? { anchor } : {}) })
            .then((p) => p, () => "error" as const),
        ),
      );

      Promise.all([totalReq, perUnitReq])
        .then(([totalP, perUnit]) => {
          if (cancelled) return;
          setTotal(totalP);
          const map: Record<number, QuotePreview | "error"> = {};
          ids.forEach((id, i) => {
            map[id] = perUnit[i]!;
          });
          setUnitPrices(map);
          setPricedKey(keyNow);
        })
        .catch(() => {
          if (cancelled) return;
          setTotal(null);
          setUnitPrices({});
          setPricedKey(null);
          setPreviewError(true);
        })
        .finally(() => {
          if (!cancelled) setPreviewing(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [units, details.sitePostcode, admin, additionalItems, careHomeItems, selectedTier]);

  // Session ACV: the ACS "tier coverage needs a lift" coercion is GONE (Director decision
  // 11 July 2026). A lift-less quote with tier-covered items is a real tiered contract --
  // the items form the plan -- so removing the last lift keeps every row's coverage as
  // the office set it, and the toggle always works.

  function updateUnit(id: number, patch: Partial<Unit>) {
    setUnits((us) => us.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }
  function addUnit() {
    setUnits((us) => (us.length >= MAX_LIFTS ? us : [...us, makeUnit()]));
  }
  function removeUnit(id: number) {
    // Session ACC Item 5: a lift may always be removed, including the last one -- a quote can consist
    // entirely of care-home equipment or additional items. The empty state shows an "Add a lift" prompt.
    setUnits((us) => us.filter((u) => u.id !== id));
  }

  const setDetail = (name: keyof typeof emptyDetails) => (e: ChangeEvent<HTMLInputElement>) =>
    setDetails((d) => ({ ...d, [name]: e.target.value }));
  const setAdminField = (name: keyof AdminTools, value: string | boolean) => setAdmin((a) => ({ ...a, [name]: value }));

  const priceFresh = total !== null && !previewError && !previewing && pricedKey === inputKey;
  const detailsOk = details.customerName.trim().length > 0;
  // Session ACC Item 5: a quote may have zero lifts, but it must price SOMETHING -- a lift, an
  // additional item or a care-home item. Mirrors the server's create guard so a totally empty
  // quote is never sent (the server would 400 otherwise).
  const hasPriceableContent = units.length > 0 || additionalItems.length > 0 || careHomeItems.length > 0;
  // Session AAP: block save/send on an invalid email (inline validation; the server also rejects it).
  const canSaveDraft = detailsOk && !saving && priceFresh && !emailInvalid && hasPriceableContent;
  const canSend = canSaveDraft && details.customerEmail.trim().length > 0;

  // ---- Autosave engine (Session AAV, Loop 2) ----
  // Debounced. Fires only for a COHERENT snapshot (canAutosave: eligible + named customer +
  // valid email + the live price settled) whose signature differs from the last autosave, via
  // the SAME draft path Save Draft uses (draft:true, revising the same quote in place), so the
  // autosaved draft is a normal draft in the Quotes list and no duplicate is ever created.
  const autosaveSig = useMemo(
    () => JSON.stringify([inputKey, details, selectedTier, linkedCustomerId, linkedSiteId]),
    [inputKey, details, selectedTier, linkedCustomerId, linkedSiteId],
  );
  // The state the calculator opened with (a resumed draft's stored form) never re-autosaves:
  // merely opening a draft to look at it must not write to the server or the pointer.
  if (initialSigRef.current === null) initialSigRef.current = autosaveSig;
  useEffect(() => {
    if (autosaveStopped.current) return;
    if (!canAutosave({ eligible: autosaveEligible, detailsOk, emailInvalid, priceFresh, saving })) return;
    // Session APK: never autosave a snapshot the server refuses (an override on the equipment-only shape);
    // the rail and the override both say why, so nothing fails in silence.
    if (admin.overrideOn && isEquipmentOnlyPriced(total, units.length)) return;
    if (autosaveSig === lastAutosavedSig.current || autosaveSig === initialSigRef.current) return;
    const t = setTimeout(() => {
      if (autosaveInFlight.current || saving || autosaveStopped.current) return;
      autosaveInFlight.current = true;
      const run = (async () => {
        try {
          const currentYear = new Date().getFullYear();
          const body = buildPricingBody(units, details.sitePostcode, admin, additionalItems, careHomeItems, currentYear, selectedTier, { customerId: linkedCustomerId, siteId: linkedSiteId, portfolioId: linkedPortfolioId });
          const calculatorState: CalculatorState = { units, details, admin, additionalItems, careHomeItems, selectedTier, linkedCustomerId, linkedSiteId };
          const cc = parseCcInput(details.ccEmails);
          const result = await api.quotes.create({
            ...body,
            customerId: linkedCustomerId ?? undefined,
            siteId: linkedSiteId ?? undefined,
            customerName: details.customerName.trim() || undefined,
            customerEmail: details.customerEmail.trim() || undefined,
            ccEmails: cc.length > 0 ? cc : undefined,
            companyNumber: details.companyNumber.trim() || undefined,
            registeredAddress: details.registeredAddress.trim() || undefined,
            siteName: details.siteName.trim() || undefined,
            siteAddress: details.siteAddress.trim() || undefined,
            preferredCommencementDate: details.preferredCommencementDate || undefined,
            recommendedTier: selectedTier,
            sendEmail: false,
            draft: true,
            autosave: true,
            id: resumeId ?? autosaveIdRef.current ?? undefined,
            calculatorState,
          });
          lastAutosavedSig.current = autosaveSig;
          autosaveIdRef.current = result.id;
          // New autosaved work supersedes any offered old draft: drop the stale banner.
          setRestoreOffer(null);
          const at = new Date();
          setAutosaveStatus({ ref: result.quoteRef, at });
          // Never resurrect a pointer after an explicit save cleared it.
          if (user && !autosaveStopped.current) {
            saveAutosavePointer(user.id, { id: result.id, ref: result.quoteRef, savedAt: at.toISOString() });
          }
        } catch (err) {
          // The draft stopped being a draft (sent from another tab / the quotes list):
          // stop autosaving for good rather than fighting the live quote. Any other
          // failure is best-effort and simply retries on the next change.
          if (err instanceof ApiError && err.status === 409) autosaveStopped.current = true;
        } finally {
          autosaveInFlight.current = false;
          autosavePromise.current = null;
          // Re-arm the debounce: a snapshot whose timer fired during this flight was
          // skipped above; bumping the tick lets the effect reschedule it.
          setAutosaveTick((n) => n + 1);
        }
      })();
      autosavePromise.current = run;
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [autosaveSig, autosaveTick, autosaveEligible, detailsOk, emailInvalid, priceFresh, saving, units, details, admin, additionalItems, careHomeItems, selectedTier, linkedCustomerId, linkedSiteId, resumeId, user, inputKey, total]);

  // Resume opens instantly from a point-in-time snapshot (whatever editQuote/the dashboard's
  // Resume/a prospect's Generate quote fetched just before navigating here). That snapshot goes
  // stale the moment a LATER save happens and the office returns to this exact route via the
  // browser's Back/Forward buttons rather than a fresh Edit click: history restores the OLD
  // navigation state verbatim, with no refetch of its own, so the form would silently show an
  // earlier edit's lift details with no indication anything is wrong (confirmed live: edit a
  // quote, save, edit it again, save again, then Back twice lands on "Editing quote X" showing
  // the FIRST edit's units, not the second save's). Re-fetch once on mount and reconcile the form
  // to it, so this screen can never display older data than the server holds, however the office
  // arrived here. A transient failure keeps the navigation-time seed rather than blanking a form
  // the office may already be looking at.
  // This read BYPASSES the request cache (Session ARU). It is the guard, so it must be able to see a
  // change the seed could not: the seed and this re-fetch are the same GET, and a change made
  // elsewhere (another office screen, the Connected AI's price override tools) never clears this
  // browser's cache, so a cached answer would reconcile the form to the same stale copy and the next
  // save would write it back over the correction.
  useEffect(() => {
    if (!resumeId) return;
    let live = true;
    api.quotes
      .get(resumeId, { noCache: true })
      .then((q) => {
        if (!live) return;
        const cs = calculatorStateForEditing(q.payload, (q.payload as { calculatorState?: CalculatorState } | null)?.calculatorState);
        if (!cs) return;
        applyCalculatorState(cs);
        // The real column wins over anything the calculatorState itself carries, matching the
        // initial-seed rule above (Session AIX): the database is authoritative for membership.
        setLinkedPortfolioId(q.portfolioId ?? cs.linkedPortfolioId ?? null);
      })
      .catch(() => {
        /* keep the navigation-time seed; a failed reconcile must not blank the form */
      });
    return () => {
      live = false;
    };
    // Mount-only: resumeId is fixed for the life of this screen (set once via useState above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore-on-return (Session AAV, Loop 2): opening a FRESH calculator (no resume, no
  // prefill) with an autosave pointer on record offers to restore that draft -- after
  // verifying it still exists and is still a draft. A pointer whose quote is gone (deleted
  // from the list) is cleared quietly; a transient failure leaves it for next time.
  useEffect(() => {
    if (!user || navState?.resume || navState?.prefill) return;
    const pointer = loadAutosavePointer(user.id);
    if (!pointer) return;
    let live = true;
    api.quotes
      .get(pointer.id)
      .then((q) => {
        if (!live) return;
        if (q.status === "draft") setRestoreOffer(pointer);
        else clearAutosavePointer(user.id);
      })
      .catch((err) => {
        if (live && err instanceof ApiError && err.status === 404) clearAutosavePointer(user.id);
      });
    return () => {
      live = false;
    };
    // Mount-only: the pointer is read once per calculator open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply a fetched/derived calculatorState to the live form fields. Shared by the mount-time
  // resync effect above and restoreDraft below, so a quote's fields are populated identically
  // regardless of which caller reached them.
  function applyCalculatorState(cs: CalculatorState) {
    setUnits(restoreUnits(cs.units));
    setSelectedTier(cs.selectedTier ?? "core");
    setDetails({ ...emptyDetails, ...(cs.details ?? {}) });
    setAdmin(cs.admin ? { ...emptyAdmin, ...cs.admin } : { ...emptyAdmin });
    setAdditionalItems(restoreRows(cs.additionalItems));
    setCareHomeItems(restoreRows(cs.careHomeItems));
    if ((cs.additionalItems?.length ?? 0) + (cs.careHomeItems?.length ?? 0) > 0) setExtrasOpen(true);
    setLinkedCustomerId(cs.linkedCustomerId ?? null);
    setLinkedSiteId(cs.linkedSiteId ?? null);
  }

  // Apply the offered draft's stored form state (the same lossless calculatorState round-trip
  // Resume uses) and keep autosaving into that same draft.
  async function restoreDraft() {
    if (!restoreOffer) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      // Live, never the request cache (Session ARU): this seeds the form and then autosaves into the
      // same draft, so it is exposed exactly as the mount re-fetch above is; and the restore-on-return
      // check above has usually just put this very quote in the cache.
      const q = await api.quotes.get(restoreOffer.id, { noCache: true });
      const cs = (q.payload as { calculatorState?: CalculatorState } | null)?.calculatorState;
      if (q.status !== "draft" || !cs) {
        if (user) clearAutosavePointer(user.id);
        setRestoreOffer(null);
        return;
      }
      // Reconciled against the quote like every other way into this form (Session AQD). A draft this
      // calculator autosaved holds a form state and a payload written by the same save, so there is
      // nothing to reconcile today; going through the one rule is what keeps that true.
      applyCalculatorState(withStoredLiftFrequencies(cs, q.payload));
      autosaveIdRef.current = restoreOffer.id;
      setAutosaveStatus({ ref: restoreOffer.ref, at: new Date(restoreOffer.savedAt) });
      // The restored state is the saved state: nothing to re-autosave until it changes.
      lastAutosavedSig.current = null;
      initialSigRef.current = null; // recaptured on the next render from the restored state
      setRestoreOffer(null);
    } catch {
      // A transient failure keeps the banner (and the pointer) so the office can retry,
      // and says so, rather than silently pretending the restore happened.
      setRestoreError("Could not load that draft just now. Try again in a moment, or start fresh.");
    } finally {
      setRestoring(false);
    }
  }

  // Start fresh: forget the pointer. The draft itself stays in the Quotes list (it is a
  // normal saved draft; delete it there if it is not wanted).
  function startFresh() {
    if (user) clearAutosavePointer(user.id);
    setRestoreOffer(null);
  }

  const submit = useCallback(
    async (mode: "draft" | "send") => {
      // Session AAQ: block the save locally when a manual override has no justification, so the
      // office gets an immediate, field-level message (the server enforces the same rule).
      // Session APK: the server refuses an override on the equipment-only shape; say so here, and before
      // asking for a reason, since on this shape the override should not be on at all.
      if (admin.overrideOn && isEquipmentOnlyPriced(total, units.length)) {
        // Answered in the action bar, where the save was pressed. Not in the reason field's slot: the reason
        // is not what is wrong, and the explanation already stands beside the override checkbox.
        setFieldErrors({ equipmentOnlyOverride: [EQUIPMENT_ONLY_OVERRIDE_NOT_SAVED] });
        setFormError(null);
        return;
      }
      if (admin.overrideOn && !admin.overrideReason.trim()) {
        setFieldErrors({ override: ["Enter a reason for the manual price override before saving."] });
        setFormError(null);
        return;
      }
      // Soft floor (Session ANR, C59): block locally, same pattern as the override above, so the
      // office gets an immediate message rather than a round trip (the server enforces the same
      // rule). Two cases: the confirmation is ticked but has no reason yet, or the live preview says
      // this quote is below the floor and the office has not confirmed it at all.
      if (admin.floorOverrideOn && !admin.floorOverrideReason.trim()) {
        setFieldErrors({ floorOverride: ["Enter a reason for pricing below the contract floor before saving."] });
        setFormError(null);
        return;
      }
      if (total?.floorBreach && !admin.floorOverrideOn) {
        setFieldErrors({ floorOverride: ["This quote prices below the contract floor. Confirm below to proceed and give a reason."] });
        setFormError(null);
        return;
      }
      setSaving(true);
      setFieldErrors({});
      setFormError(null);
      // Serialise against the background autosave (Session AAV): stop further autosaves, then
      // AWAIT any in-flight one so its created draft id is known -- the explicit save then
      // revises THAT draft instead of racing it into a duplicate quote.
      autosaveStopped.current = true;
      if (autosavePromise.current) {
        try {
          await autosavePromise.current;
        } catch {
          /* the autosave's own error handling applies */
        }
      }
      try {
        const currentYear = new Date().getFullYear();
        const body = buildPricingBody(units, details.sitePostcode, admin, additionalItems, careHomeItems, currentYear, selectedTier, { customerId: linkedCustomerId, siteId: linkedSiteId, portfolioId: linkedPortfolioId });
        const calculatorState: CalculatorState = { units, details, admin, additionalItems, careHomeItems, selectedTier, linkedCustomerId, linkedSiteId };
        const result = await api.quotes.create({
          ...body,
          // Session AAP: link an existing customer/site chosen from the autocomplete (drives the
          // Companies House number write to a linked customer, and reuse of the site record).
          customerId: linkedCustomerId ?? undefined,
          siteId: linkedSiteId ?? undefined,
          customerName: details.customerName.trim() || undefined,
          customerEmail: details.customerEmail.trim() || undefined,
          ccEmails: parseCcInput(details.ccEmails).length > 0 ? parseCcInput(details.ccEmails) : undefined,
          companyNumber: details.companyNumber.trim() || undefined,
          registeredAddress: details.registeredAddress.trim() || undefined,
          siteName: details.siteName.trim() || undefined,
          siteAddress: details.siteAddress.trim() || undefined,
          preferredCommencementDate: details.preferredCommencementDate || undefined,
          recommendedTier: selectedTier,
          sendEmail: mode === "send",
          draft: mode === "draft",
          // REVISE the resumed draft (or the draft the autosave created) in place, keeping
          // its ref; else create a new quote. Read from the REF (not the render closure) so
          // an autosave that completed during the await above is honoured. This is why an
          // explicit save after autosaves never duplicates the quote.
          id: resumeId ?? autosaveIdRef.current ?? undefined,
          calculatorState,
        });
        // The quote is now explicitly saved/sent: the autosave pointer has done its job.
        if (user) clearAutosavePointer(user.id);
        // Session APK: confirm the tier the server RECORDED, not the one this page last had selected. They
        // differ on an equipment-only quote, which the server always saves as Core: its tier buttons are
        // hidden here, so selectedTier can still hold a tier picked while a lift was on the quote, and the
        // confirmation read "Care at £320/yr" for a quote with no tier at all.
        const savedTier = TIERS.find((t) => t.value === result.recommendedTier)?.value ?? selectedTier;
        const recommendedAnnual = result.pricing?.tiers?.[savedTier]?.annual ?? null;
        navigate("/office/quotes", {
          state: {
            created: {
              quoteRef: result.quoteRef,
              token: result.token,
              emailed: result.emailed,
              email: details.customerEmail.trim(),
              recommendedLabel: TIERS.find((t) => t.value === savedTier)?.label ?? "Core",
              annual: recommendedAnnual,
            },
          },
        });
      } catch (err) {
        if (err instanceof ApiError && err.details) {
          setFieldErrors(err.details);
        } else if (err instanceof ApiError) setFormError(friendlyApiError(err, "Could not save the quote. Please try again."));
        else setFormError("Could not save the quote. Please try again.");
        setSaving(false);
        // The explicit save failed, so the office is still editing: let autosave resume.
        autosaveStopped.current = false;
      }
    },
    [units, details, admin, additionalItems, careHomeItems, selectedTier, navigate, resumeId, linkedCustomerId, linkedSiteId, user, total],
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Session AAV: the form can be submitted by pressing Enter on the focused Send button (or
    // Ctrl/Cmd+Enter below); the same guard as the button's disabled state applies, so a
    // keyboard submit can never send an unpriceable or email-less quote.
    if (!canSend) return;
    submit("send");
  };

  // ---- Keyboard-first flow (Session AAV, Loop 2) ----
  // Plain Enter in a text input never submits (an accidental send from the middle of the form
  // was possible before); Ctrl/Cmd+Enter sends from anywhere when the quote is sendable.
  function onFormKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    if (e.key !== "Enter") return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (canSend) void submit("send");
      return;
    }
    if (e.target instanceof HTMLInputElement) e.preventDefault();
  }

  // Escape is two-step: the first leaves the focused element (closing any autocomplete
  // dropdown; a focused button blurs too, so a mouse-click focus never makes Escape close in
  // one step), the second closes the calculator back to the quotes list. A WINDOW listener,
  // not a container onKeyDown: after a blur the next keydown targets document.body, which a
  // React tree handler never receives. While the templates modal is open it owns Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || templatesOpen) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && el !== document.body && typeof el.blur === "function") {
        el.blur();
        return;
      }
      navigate("/office/quotes");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [templatesOpen, navigate]);

  // The command palette's "New quote from template" can fire while the calculator is already
  // mounted (same route, new query string): honour ?templates=1 whenever it appears, not just
  // in the mount initializer.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("templates") === "1") setTemplatesOpen(true);
  }, [location.search]);

  // Session AAT Loop 4: the current calculator configuration to save as a template -- lifts, tier,
  // admin tools and additional/care items, WITHOUT any customer/site identity (that is filled fresh
  // per quote). Applying a template restores these into the calculator (identity untouched).
  const templateConfig = useMemo(
    () => ({ units, admin, additionalItems, careHomeItems, selectedTier }),
    [units, admin, additionalItems, careHomeItems, selectedTier],
  );

  function applyTemplate(config: Record<string, unknown>) {
    const c = config as Partial<CalculatorState>;
    setUnits(restoreUnits(c.units));
    setSelectedTier(c.selectedTier ?? "core");
    setAdmin(c.admin ? { ...emptyAdmin, ...c.admin } : { ...emptyAdmin });
    setAdditionalItems(restoreRows(c.additionalItems));
    setCareHomeItems(restoreRows(c.careHomeItems));
    // A template that carries line items opens the extras section so they are seen.
    if ((c.additionalItems?.length ?? 0) + (c.careHomeItems?.length ?? 0) > 0) setExtrasOpen(true);
  }

  // Load the tenant's labour cost per hour once, for the office-only margin display.
  useEffect(() => {
    let live = true;
    api.settings
      .tenant()
      .then((s) => live && setLabourCostPerHour(s.labourCostPerHour))
      .catch(() => undefined); // best-effort: the margin panel simply omits if unavailable
    return () => {
      live = false;
    };
  }, []);

  // Office-only per-tier margin (price minus estimated labour + parts cost). Never stored, never
  // sent to the customer. Null until the labour cost + a live price are available.
  const margins = useMemo(
    () =>
      total && labourCostPerHour != null
        ? estimateQuoteMargins({ tiers: total.tiers, serviceHoursYr: total.serviceHoursYr ?? 0, numLifts: total.numLifts, labourCostPerHour })
        : null,
    [total, labourCostPerHour],
  );

  const discountNote = useMemo(
    () => (total ? multiUnitDiscountNote(total.numLifts, total.multiDiscount, total.discountLiftCount) : null),
    [total],
  );
  // Session AHC: name the two halves of the portfolio count when it is what earned the band, so a
  // figure that moves money can be checked. Silent when this quote stands on its own lifts.
  const portfolioSplit = useMemo(() => {
    const b = total?.portfolioBreakdown;
    if (!b || !total || (total.discountLiftCount ?? 0) <= total.numLifts) return null;
    const parts: string[] = [];
    if (b.contractedLifts > 0) parts.push(`${b.contractedLifts} under signed contract`);
    if (b.quotedLifts > 0) parts.push(`${b.quotedLifts} on grouped quotes`);
    return parts.length ? `Portfolio total: ${parts.join(" plus ")}.` : null;
  }, [total]);
  const multiDiscountActive = !!total && total.multiDiscount > 0;
  const uniformFreq: QuoteFrequency | null =
    units.length > 0 && units.every((u) => u.frequency === units[0]!.frequency) ? units[0]!.frequency : null;
  const suggestedRef = useMemo(() => quoteRefFromSite(details.siteName, new Date()), [details.siteName]);
  // Session APK: Core on the equipment-only shape (see shownTier below, which this line precedes).
  const band = total ? costFloorBand(total.tiers[isEquipmentOnlyPriced(total, units.length) ? "core" : selectedTier].annual, total.costFloor) : null;
  // Session ACK: the office-only anchor floor note. The contract floor is anchor-independent, so
  // lowering the anchor below a threshold has no visible effect; this explains that to the office.
  const floorInfo = useMemo(
    () => anchorFloorInfo(units.map((u) => ({ liftType: u.liftType, frequency: u.frequency }))),
    [units],
  );
  const extrasCount = additionalItems.length + careHomeItems.length;
  // Session ABC Loop 3: the customer & site block collapses to a summary ONLY when a linked
  // site filled everything the send needs (named customer + a valid email on record) and
  // nothing is in error; otherwise the fields stay open so a gap is never hidden.
  const whoForErrors =
    emailInvalid ||
    !!(fieldErrors.customerName || fieldErrors.customerEmail || fieldErrors.siteName || fieldErrors.siteAddress || fieldErrors.sitePostcode);
  const whoForCollapsed =
    linkedSiteId != null && !whoForExpanded && !whoForErrors && details.customerName.trim() !== "" && details.customerEmail.trim() !== "";

  // ---- Session ACS: additional items split by the tier toggle ----
  // A COVERED (tiers-on) row lives INSIDE the tier cards -- each card's price includes
  // the item at that tier's scaled figure, itemised in the breakdown. A FLAT (tiers-off)
  // row gets the separated extras treatment, like the equipment block: its own labelled
  // block, the same figure on every plan, maintenance-only.
  const coveredBlock = total?.additionalCovered ?? null;
  const hasCovered = (coveredBlock?.rows.length ?? 0) > 0;

  // ---- The rail shapes (ACP spec 2.4; Session ACV adds the gates-only tiered fork) ----
  // Lift present: three tier cards, as today (covered items inside the plan figures).
  // No lift + at least one COVERED item: the tier cards render too -- the ITEMS form the
  // plan (Core = the entered prices, Care/Complete scaled), so this is NOT the
  // equipment-only shape even when equipment is also on the quote.
  // No lift + no covered item: the equipment-only single-price shape, unchanged.
  const equipmentRows = total?.equipment?.rows ?? [];
  const hasEquipment = equipmentRows.length > 0;
  const equipmentOnly = isEquipmentOnlyPriced(total, units.length);
  // Session APK: the tier this rail SHOWS a price for. The equipment-only shape has no tier and the
  // server records it as Core, so the rail reads Core there; selectedTier can still hold a tier picked
  // while a lift was on the quote (the tier buttons are hidden on this shape) and is kept for when a lift
  // is added back.
  const shownTier: Tier = equipmentOnly ? "core" : selectedTier;
  // Session APK: a manual override on this shape is refused on save (EQUIPMENT_ONLY_OVERRIDE_MESSAGE).
  const equipmentOnlyOverride = equipmentOnly && admin.overrideOn;
  const flatAdditional = (total?.additionalItems ?? []).filter((it) => it.tiersOn !== true);
  const hasFlatAdditional = flatAdditional.length > 0;
  // The per-row inline prices for a covered row: matched by ORDER among the valid
  // covered rows, exactly the order the priced body sent them (descriptions can repeat,
  // ids cannot). Null while the preview lags or the row is not priced yet.
  const coveredPriceFor = (row: LineRow): { core: number; care: number; complete: number } | null => {
    if (row.tiersOn !== true) return null;
    const validCovered = additionalItems.filter(
      (r) => r.tiersOn === true && r.description.trim() !== "" && Number.parseFloat(r.price) > 0,
    );
    const k = validCovered.findIndex((r) => r.id === row.id);
    return k >= 0 ? coveredBlock?.rows[k]?.annual ?? null : null;
  };
  // Session ACS item A: the equipment single-item site minimum, mirrored client-side over
  // the PRICED equipment rows (so the row count matches the server's view) for the LIVE
  // per-row figure; the rail note reads the server's authoritative flag (total.equipment).
  // Session ACV: a PRICED covered additional item disapplies the minimum only when its
  // cadence covers every equipment visit, mirroring the server rule (max covered visits).
  const anchorNum = Number.parseFloat(admin.anchor);
  const equipSingleMin = equipmentSingleItemMinimum(
    units.length,
    toLineItems(careHomeItems, true),
    Number.isFinite(anchorNum) && anchorNum > 0 ? anchorNum : 160,
    toLineItems(additionalItems, false, units.length === 0)
      .filter((it) => it.tiersOn === true)
      .reduce((m, it) => Math.max(m, Number(it.serviceVisitsYr) || ADDITIONAL_SERVICE_DEFAULT_VISITS), 0),
  );

  return (
    <div className="ele-calc fixed inset-0 z-50 flex flex-col overflow-hidden bg-ele-base text-ele-body">
      {/* Branded header band (ACP, spec 3.3): the ELE logo at 26px, the Rajdhani title and
          the locked tri-colour gradient bottom rule -- .ele-office-header::after applies via
          the .office-ui ancestor, the same signature as the office header, so the takeover
          reads as ELE, not a generic modal. Fixed height (h-12, matching the office band). */}
      <header className="ele-office-header relative flex h-12 shrink-0 items-center justify-between gap-3 bg-[var(--ele-shell)] px-4">
        <div className="flex min-w-0 items-center gap-3">
          <img src="/brand/ele-logo.png" alt="East London Elevators" className="h-[26px] w-auto shrink-0" />
          <h1 className="shrink-0 font-heading text-lg uppercase tracking-wide text-ele-strong">Quote calculator</h1>
          {/* The editing indicator is never hidden (an office user must always see they are
              revising, not creating); the fresh-quote hint alone yields on narrow screens. */}
          <span className={`truncate text-xs text-ele-body opacity-70 ${editingRef ? "" : "hidden sm:inline"}`}>
            {editingRef
              ? `Editing quote ${editingRef}. Saving or resending updates it in place; the customer receives a fresh copy.`
              : "Configure the lifts and equipment; the live prices stay in view on the right."}
          </span>
        </div>
        <button
          type="button"
          onClick={close}
          className="shrink-0 rounded-ele border border-ele-purple-accent px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:border-ele-danger hover:text-ele-danger"
        >
          Close
        </button>
      </header>

      {/* Body + the always-visible action bar below it. SPLIT SCROLL (ACP, spec 3.1):
          below lg this stays the single stacked scroll flow it has always been; on lg+
          the body becomes a flex row with a bounded height (min-h-0) whose two children
          -- the form column and the pricing rail -- each own their OWN scrollbar. The
          rail never moves when the form scrolls because it is not in the form's scroll
          container at all. NO sticky positioning anywhere in this layout. */}
      <form onSubmit={onSubmit} onKeyDown={onFormKeyDown} className="flex flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto lg:flex lg:min-h-0 lg:flex-row lg:overflow-hidden">
          {/* LEFT: the form column. Full window width on desktop (the max-w-6xl cap is
              lifted to a comfortable ~1280px reading width, centred in the column on
              ultra-wide monitors); sub-lg keeps the exact stacked behaviour. */}
          <div className="p-6 pb-0 lg:min-w-0 lg:flex-1 lg:overflow-y-auto lg:pb-6">
          <div className="mx-auto max-w-6xl lg:max-w-[1280px]">
          {/* Restore-on-return (Session AAV): an autosaved draft from a previous visit. */}
          {restoreOffer && (
            <div role="status" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-ele border border-ele-blue bg-ele-surface p-4">
              <p className="text-sm text-ele-body">
                An unsaved quote <span className="text-ele-strong">{restoreOffer.ref}</span> was autosaved{" "}
                {formatDateTime(restoreOffer.savedAt)}.
                Restore it here, or start fresh. Either way it stays in your Quotes list as a draft.
                {restoreError && <span role="alert" className="block text-ele-danger">{restoreError}</span>}
              </p>
              <div className="flex shrink-0 gap-2">
                <Button variant="secondary" size="sm" onClick={() => void restoreDraft()} disabled={restoring}>
                  {restoring ? "Restoring..." : "Restore draft"}
                </Button>
                <button
                  type="button"
                  onClick={startFresh}
                  className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
                >
                  Start fresh
                </button>
              </div>
            </div>
          )}
            <div className="space-y-3">
              {/* ---- Power tools toolbar (compact). AI site assist, scenario comparison and
                   quote templates. Moved here from the old full-width "More tools" section so
                   the page is short (Session ACM); each keeps its exact behaviour, only its
                   footprint changed. A tool's panel opens on demand below this row. ---- */}
              <div className="flex flex-wrap items-start gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2">
                <span className="self-center font-heading text-xs uppercase tracking-wide text-ele-blue">Tools</span>
                <SiteConfigAssist onApply={(newUnits) => setUnits(newUnits.slice(0, MAX_LIFTS))} />
                <ScenarioComparison body={buildPricingBody(units, details.sitePostcode, admin, additionalItems, careHomeItems, new Date().getFullYear(), selectedTier, { customerId: linkedCustomerId, siteId: linkedSiteId, portfolioId: linkedPortfolioId })} />
                <button
                  type="button"
                  onClick={() => setTemplatesOpen(true)}
                  className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
                >
                  Templates
                </button>
              </div>

              {/* ---- Customer & site (first: who the quote is for) ---- */}
              <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <h2 className="font-heading text-lg text-ele-blue">Customer &amp; site</h2>
                  <span className="text-xs text-ele-body opacity-70">
                    Suggested reference: <span className="text-ele-strong">{suggestedRef}</span> (allocated on save)
                  </span>
                </div>
                <p className="mb-4 text-xs text-ele-body opacity-70">
                  Type to search existing customers and sites; picking one fills every known detail and links the quote.
                  {(linkedCustomerId || linkedSiteId) && (
                    <span className="ml-1 text-ele-green">Linked to an existing {linkedSiteId ? "site" : "customer"}.</span>
                  )}
                </p>
                {whoForCollapsed ? (
                  /* Session ABC Loop 3: the prefill filled everything, so show it as one calm
                     summary instead of five fields. Change re-opens the fields with every value
                     intact; the surcharge notice stays live off the filled postcode. */
                  <div className="flex flex-wrap items-start justify-between gap-3 rounded-ele border border-ele-purple-accent bg-ele-bg/40 p-4">
                    <div>
                      <p className="font-heading text-sm uppercase tracking-wide text-ele-strong">{details.siteName}</p>
                      <p className="mt-0.5 text-xs text-ele-body">
                        {[details.siteAddress, details.sitePostcode].filter(Boolean).join(" · ")}
                      </p>
                      <p className="mt-1 text-xs text-ele-body">
                        {details.customerName}
                        {details.customerEmail ? <span className="opacity-70"> · {details.customerEmail}</span> : null}
                      </p>
                      <p role="status" aria-live="polite" className={`mt-1 text-xs ${postcodeNotice ? "text-ele-blue" : "text-ele-body opacity-70"}`}>
                        {postcodeNotice ?? ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setWhoForExpanded(true)}
                      className="shrink-0 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <RecordComboField
                    label="Customer name"
                    name="customerName"
                    value={details.customerName}
                    onChange={onCustomerNameChange}
                    onFocus={() => setCustomerFocused(true)}
                    onBlur={() => setCustomerFocused(false)}
                    onPick={pickCustomer}
                    items={customerMatches.map((c) => ({ id: c.id, primary: c.name, secondary: [c.companyNumber, c.email].filter(Boolean).join(" · ") || null }))}
                    searching={customerSearching}
                    open={customerFocused}
                    required
                    hint="Type to search existing customers, or enter a new one."
                    errors={fieldErrors.customerName}
                  />
                  <Field
                    label="Customer email"
                    name="customerEmail"
                    type="email"
                    value={details.customerEmail}
                    onChange={setDetail("customerEmail")}
                    errors={emailInvalid ? ["Enter a valid email address"] : fieldErrors.customerEmail}
                  />
                  <RecordComboField
                    label="Site name"
                    name="siteName"
                    value={details.siteName}
                    onChange={onSiteNameChange}
                    onFocus={() => setSiteNameFocused(true)}
                    onBlur={() => setSiteNameFocused(false)}
                    onPick={pickSite}
                    items={siteMatches.map((s) => ({ id: s.id, primary: s.name, secondary: [s.address, s.customerName].filter(Boolean).join(" · ") || null }))}
                    searching={siteSearching}
                    open={siteNameFocused}
                    hint="Search existing sites or enter a new one."
                    errors={fieldErrors.siteName}
                    autoFocus={freshOpen}
                  />
                  <AddressAutocompleteField
                    value={details.siteAddress}
                    onChange={setDetail("siteAddress")}
                    onFocus={() => setAddrFocused(true)}
                    onBlur={() => setAddrFocused(false)}
                    onPick={pickAddressSuggestion}
                    suggestions={addrSuggestions}
                    searching={addrSearching}
                    open={addrFocused}
                    errors={fieldErrors.siteAddress}
                  />
                  <div>
                    <Field label="Site postcode" name="sitePostcode" value={details.sitePostcode} onChange={setDetail("sitePostcode")} errors={fieldErrors.sitePostcode} placeholder="e.g. EC1A 1BB" />
                    {/* Always-mounted live region (a conditionally-mounted role=status is not
                        reliably announced). Empty postcode shows the standing hint -- the 3 July
                        live-test misses were quotes whose postcode field was simply left blank. */}
                    <p role="status" aria-live="polite" className={`mt-1 text-xs ${postcodeNotice ? "text-ele-blue" : "text-ele-body opacity-70"}`}>
                      {postcodeNotice ??
                        (details.sitePostcode.trim() === ""
                          ? "No site postcode entered. Congestion charge and parking surcharges are only applied when the postcode is filled in."
                          : "")}
                    </p>
                    {/* Session AAP: free postcode validation. Green confirmation with the resolved
                        town/district/region when postcodes.io recognises it; a quiet note otherwise.
                        The surcharge notice above is independent (client mirror), so pricing is
                        unaffected whether or not this lookup resolves. */}
                    {postcodeChecking ? (
                      <p className="mt-1 text-xs text-ele-body opacity-70">Checking postcode...</p>
                    ) : resolvedPlace && placeSummary(resolvedPlace) ? (
                      <p className="mt-1 text-xs text-ele-green">✓ {placeSummary(resolvedPlace)}</p>
                    ) : postcodeUnknown ? (
                      <p className="mt-1 text-xs text-amber-400">Postcode not recognised. You can still enter it manually.</p>
                    ) : null}
                  </div>
                </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <CompanyLookupField
                        query={companyQuery}
                        onQueryChange={(e) => setCompanyQuery(e.target.value)}
                        onFocus={() => setCompanyFocused(true)}
                        onBlur={() => setCompanyFocused(false)}
                        onPick={pickCompany}
                        results={companyResults}
                        searching={companySearching}
                        configured={companyConfigured}
                        picking={companyPicking}
                        open={companyFocused}
                      />
                    </div>
                    <Field
                      label="Copy recipients (optional)"
                      name="ccEmails"
                      value={details.ccEmails}
                      onChange={setDetail("ccEmails")}
                      errors={fieldErrors.ccEmails}
                      placeholder="They get the quote and accept link but do not sign. Comma separated."
                    />
                    <Field label="Company number" name="companyNumber" value={details.companyNumber} onChange={setDetail("companyNumber")} errors={fieldErrors.companyNumber} placeholder="e.g. 15429089" />
                    <Field label="Registered office address" name="registeredAddress" value={details.registeredAddress} onChange={setDetail("registeredAddress")} errors={fieldErrors.registeredAddress} placeholder="The client's Companies House registered office" />
                    <Field
                      label="Preferred commencement date"
                      name="preferredCommencementDate"
                      type="date"
                      value={details.preferredCommencementDate}
                      onChange={setDetail("preferredCommencementDate")}
                      errors={fieldErrors.preferredCommencementDate}
                    />
                  </div>
                  </>
                )}
              </section>

              {/* ---- Configuration ---- */}
              <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="font-heading text-lg text-ele-blue">Lift configuration</h2>
                <span className="text-xs text-ele-body opacity-70">
                  {units.length} {units.length === 1 ? "unit" : "units"}
                </span>
              </div>

              <div className="space-y-3">
                {units.map((u, i) => {
                  const up = unitPrices[u.id];
                  // Item 4 (Session ACC): the per-lift override is the lift's custom CORE base price;
                  // Care and Complete scale proportionally from it on the server. The pre-fill is this
                  // lift's TRUE in-quote CORE contribution -- the DISCOUNTED per-lift figure from the full
                  // preview (total.perLift[i].core), NOT the undiscounted priced-alone `up` -- so on a
                  // multi-lift discounted quote, leaving the pre-filled value unchanged is a genuine no-op,
                  // never silently inflating the site total. Falls back to `up` before the full preview resolves.
                  const overrideBaseline = total?.perLift?.[i]?.core;
                  const custom = isCustomKey(u.liftType);
                  // Session AHA: the stop count auto-sets the visit duration (Director-locked), and
                  // that multiplier is real money. Say why it reads as it does, and flag a manual
                  // pick the stop count does not imply. Null from 1 to 6 stops (Standard, x1.0).
                  const durationHint = durationHintForStops(parseStops(u.stops), u.duration);
                  return (
                    <div key={u.id} className="rounded-ele border border-ele-purple-accent bg-ele-bg/40 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="font-heading text-sm uppercase tracking-wide text-ele-strong">Unit {i + 1}{u.label.trim() ? `: ${u.label.trim()}` : ""}</span>
                        {/* Session ACC Item 5: every lift is removable, including the last one. */}
                        <button type="button" onClick={() => removeUnit(u.id)} className="text-xs text-ele-danger underline hover:opacity-80">
                          Remove
                        </button>
                      </div>
                      {/* The priced inputs lead: type, frequency, stops, year (and the custom
                          multiplier for a custom type), with the descriptive fields in the grid
                          below. Session ABC put those behind a per-unit disclosure; it has since
                          been removed and they render unconditionally (comment corrected in
                          Session AHA, which found it still describing a collapse that is gone). */}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <SelectField
                          label="Type"
                          name={`liftType-${u.id}`}
                          value={u.liftType}
                          onChange={(e) => {
                            const liftType = e.target.value;
                            // Changing the type can make an annual cadence non-compliant
                            // (only goods/low-traffic types allow it); coerce it down to twice yearly if so.
                            updateUnit(u.id, { liftType, frequency: coerceFrequencyForType(liftType, u.frequency) });
                          }}
                          options={LIFT_TYPE_OPTIONS}
                        />
                        {custom && (
                          <NumberField
                            label="Custom multiplier"
                            name={`customMult-${u.id}`}
                            value={u.customMultiplier}
                            min={0.1}
                            max={5}
                            step={0.01}
                            onChange={(e) => updateUnit(u.id, { customMultiplier: e.target.value })}
                          />
                        )}
                        <SelectField
                          label="Service frequency"
                          name={`frequency-${u.id}`}
                          value={u.frequency}
                          onChange={(e) => updateUnit(u.id, { frequency: e.target.value as QuoteFrequency })}
                          options={frequencyOptionsForType(u.liftType)}
                        />
                        <NumberField
                          label="Number of stops"
                          name={`stops-${u.id}`}
                          value={u.stops}
                          min={2}
                          // Floor count drives the duration (Director-locked): re-apply durationForFloors
                          // on every change, UNCONDITIONALLY. That is what hands the rule back after a
                          // manual pick; clearing manualDurationOverride below does not cause it and
                          // nothing reads that flag (see the Unit interface).
                          onChange={(e) =>
                            updateUnit(u.id, {
                              stops: e.target.value,
                              duration: durationForFloors(parseStops(e.target.value)),
                              manualDurationOverride: false,
                              // Session AIE: the office has supplied a real stop count and the
                              // duration is derived from it, so BOTH facts are now decided and both
                              // are written to the payload from here on (see the flags on Unit).
                              stopsUnrecorded: false,
                              durationUnrecorded: false,
                            })
                          }
                        />
                        <YearComboField
                          name={`year-${u.id}`}
                          value={u.year}
                          onChange={(year) => updateUnit(u.id, { year })}
                        />
                      </div>
                      {doorTypeIsManual(u.doorType) && (
                        <p className="mt-2 text-xs text-ele-blue">Manual doors add £10 per stop per visit.</p>
                      )}
                        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <TextField label="Lift name" name={`name-${u.id}`} value={u.label} optional placeholder="e.g. Main passenger lift" onChange={(e) => updateUnit(u.id, { label: e.target.value })} />
                          <div>
                            <SelectField
                              label="Visit duration"
                              name={`duration-${u.id}`}
                              value={u.duration}
                              // A manual pick is respected until the floor count changes again, which
                              // re-applies the rule. That falls out of only ever writing duration in the
                              // stops handler, not from reading manualDurationOverride (see the interface).
                              // Session AIE: a manual pick is a decision, so the duration is written
                              // on save from here on. The stop count is untouched by this handler
                              // and keeps its own flag: the two facts are independent.
                              onChange={(e) => updateUnit(u.id, { duration: e.target.value as QuoteDuration, manualDurationOverride: true, durationUnrecorded: false })}
                              options={DURATION_OPTIONS}
                            />
                            {durationHint && (
                              <p
                                data-testid={`duration-hint-${u.id}`}
                                className={`mt-1 text-xs ${durationHint.matchesRule ? "text-ele-blue" : "text-amber-400"}`}
                              >
                                {durationHint.text}
                              </p>
                            )}
                          </div>
                          <SelectField
                            label="Door type"
                            name={`door-${u.id}`}
                            value={u.doorType}
                            onChange={(e) => updateUnit(u.id, { doorType: e.target.value })}
                            options={DOOR_TYPE_OPTIONS}
                          />
                          <DatalistField
                            label="Capacity"
                            name={`capacity-${u.id}`}
                            value={u.capacity}
                            suggestions={CAPACITY_SUGGESTIONS}
                            placeholder="e.g. 8 person / 630kg"
                            onChange={(e) => updateUnit(u.id, { capacity: e.target.value })}
                          />
                          <SelectField
                            label="Controller"
                            name={`controller-${u.id}`}
                            value={u.controller}
                            onChange={(e) => updateUnit(u.id, { controller: e.target.value })}
                            options={CONTROLLER_OPTIONS}
                          />
                          <ManufacturerField
                            name={`mfr-${u.id}`}
                            value={u.manufacturer}
                            onChange={(e) => updateUnit(u.id, { manufacturer: e.target.value })}
                          />
                          <TextField
                            label="Serial number"
                            name={`serial-${u.id}`}
                            value={u.serial}
                            optional
                            onChange={(e) => updateUnit(u.id, { serial: e.target.value })}
                          />
                        </div>
                      {/* This unit's own price (all three tiers), straight from the engine. */}
                      <div className={`mt-3 border-t border-ele-purple-accent pt-3 text-xs ${previewing ? "opacity-60" : ""}`}>
                        {previewError || up === "error" ? (
                          <span className="text-ele-body opacity-70">Price unavailable</span>
                        ) : up === undefined ? (
                          <span className="text-ele-body opacity-70">Pricing this unit...</span>
                        ) : (
                          <span className="text-ele-body">
                            This unit on its own:{" "}
                            <span className="text-ele-strong">Core {gbp(up.tiers.core.annual)}</span>
                            {" · "}
                            <span className="text-ele-strong">Care {gbp(up.tiers.care.annual)}</span>
                            {" · "}
                            <span className="text-ele-strong">Complete {gbp(up.tiers.complete.annual)}</span>
                            {multiDiscountActive && <span className="opacity-70"> (before the multi-unit discount)</span>}
                          </span>
                        )}
                      </div>
                      {/* Item 4 (Session ACC): a per-lift MANUAL override. It sets the lift's FINAL Core
                          price -- the amount already includes the multi-lift discount and any manual-door
                          fee, and bypasses the discount for this lift (Session ACF label clarification, after
                          the office typed pre-discount figures by mistake). Care and Complete scale
                          proportionally on the server. The calculated engine prices stay visible so the
                          office sees the delta; clearing reverts to engine pricing. */}
                      {up !== undefined && up !== "error" && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                          {u.priceOverride.trim() === "" ? (
                            <button
                              type="button"
                              onClick={() => updateUnit(u.id, { priceOverride: String(Math.round(overrideBaseline ?? up.tiers.core.annual)) })}
                              className="text-ele-blue underline"
                            >
                              Override this lift's final Core price
                            </button>
                          ) : (
                            <>
                              <label htmlFor={`override-${u.id}`} className="text-ele-body">
                                Final Core price for this lift £
                              </label>
                              <input
                                id={`override-${u.id}`}
                                type="number"
                                inputMode="numeric"
                                min={0}
                                value={u.priceOverride}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => updateUnit(u.id, { priceOverride: e.target.value })}
                                className="w-24 rounded-ele border border-ele-green bg-ele-field px-2 py-1 text-ele-strong outline-none"
                              />
                              <span className="text-ele-body opacity-70">
                                calculated Core {gbp(up.tiers.core.annual)} · Care {gbp(up.tiers.care.annual)} · Complete {gbp(up.tiers.complete.annual)}
                              </span>
                              <button type="button" onClick={() => updateUnit(u.id, { priceOverride: "" })} className="text-ele-danger underline">
                                Clear
                              </button>
                              <span className="w-full text-ele-body opacity-70">
                                Enter the final price for this lift, including any multi-lift discount and door fee, not a pre-discount base.
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Session ACC Item 5: with no lifts, show an invitation rather than a locked empty
                    card. The quote can still be sent on care-home equipment or additional items alone. */}
                {units.length === 0 && (
                  <div className="rounded-ele border border-dashed border-ele-purple-accent bg-ele-bg/40 p-6 text-center">
                    <p className="text-sm text-ele-body">No lifts on this quote.</p>
                    <p className="mt-1 text-xs text-ele-body opacity-70">
                      Add a lift, or price care-home equipment or additional items only.
                    </p>
                    <Button variant="secondary" onClick={addUnit} className="mt-3">
                      + Add a lift
                    </Button>
                  </div>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button variant="secondary" onClick={addUnit} disabled={units.length >= MAX_LIFTS}>
                  {units.length === 0 ? "+ Add a lift" : "+ Add another unit"}
                </Button>
                {units.length >= MAX_LIFTS && <span className="text-xs text-ele-body opacity-70">Maximum {MAX_LIFTS} units per quote.</span>}
              </div>
              <p className="mt-3 text-xs text-ele-body opacity-70">Each unit is priced and serviced at its own frequency. The contract bills on the shortest one.</p>
              </section>

              {/* ---- Additional items + care-home equipment (on demand) ---- */}
              <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-heading text-sm uppercase tracking-wide text-ele-blue">Additional items and care-home equipment</p>
                    <p className="mt-1 text-xs text-ele-body opacity-70">
                      {extrasCount > 0
                        ? `${extrasCount} line ${extrasCount === 1 ? "item" : "items"} on this quote.`
                        : "Doors, gates, traffic lights, hoists and other equipment. Added to every tier, never discounted."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setExtrasOpen((o) => !o)}
                    aria-expanded={extrasOpen}
                    className="shrink-0 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
                  >
                    {extrasOpen ? "Hide" : extrasCount > 0 ? "Show items" : "Add items"}
                  </button>
                </div>
                {extrasOpen && (
                  <>
                    <LineItemSection
                      title="Additional items"
                      hint="One-off line items (doors, gates, traffic lights), never multi-unit discounted. Maintenance-only by default; switch a row's Tier coverage ON to price it into the plans (the price is its Core figure; Care and Complete scale with the plan and its cover follows the chosen tier). With no lifts on the quote, the covered items form the plan themselves and each carries its own service frequency."
                      rows={additionalItems}
                      setRows={setAdditionalItems}
                      presets={ADDITIONAL_ITEM_PRESETS}
                      presetLabel="Add a common item"
                      tierToggle
                      liftless={units.length === 0}
                      coveredPriceFor={coveredPriceFor}
                    />
                    <LineItemSection
                      title="Care-home equipment"
                      hint="Hoists, stand aids, tables. Maintenance only, no tiers, added to the site total, never discounted. The price is the yearly rate at the item's minimum service frequency (six-monthly where the item lifts a person); stepping the frequency up charges added visits at a reduced rate."
                      rows={careHomeItems}
                      setRows={setCareHomeItems}
                      presets={CARE_HOME_EQUIPMENT_PRESETS}
                      presetLabel="Add equipment"
                      equipment
                      singleItemMinimum={equipSingleMin}
                    />
                  </>
                )}
              </section>

              {/* ---- Pricing tools (office: anchor, override, surcharge, promo). Flat and
                   visible per Director; compacted to a tight band. AI assist, scenario
                   comparison and templates moved to the top toolbar (Session ACM). ---- */}
              <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <p className="font-heading text-sm uppercase tracking-wide text-ele-blue">Pricing tools (office)</p>
                <div className="mt-3 flex flex-wrap items-start gap-x-6 gap-y-3">
                  {/* Anchor + its office-only floor note (Session ACK). */}
                  <div className="w-40">
                    <NumberField label="Base £/visit (anchor)" name="anchor" value={admin.anchor} min={1} step={1} onChange={(e) => setAdminField("anchor", e.target.value)} />
                    {floorInfo && (
                      <p className="mt-1 text-[11px] text-ele-body opacity-70">
                        {floorInfo.kind === "exempt"
                          ? "No floor applies - this lift type is floor-exempt."
                          : `Floor: ${gbp(floorInfo.floor)}/yr at ${floorInfo.visitsYr} visits/yr. Anchor below ${gbp(floorInfo.perVisitFloor)} has no effect.`}
                      </p>
                    )}
                  </div>
                  {/* Session AAQ: per-quote congestion/parking surcharge presentation (Director decision). */}
                  <div className="w-60">
                    <SelectField
                      label="Congestion / parking surcharge"
                      name="surchargePresentation"
                      value={admin.surchargePresentation}
                      onChange={(e) => setAdminField("surchargePresentation", e.target.value)}
                      options={SURCHARGE_PRESENTATION_OPTIONS}
                    />
                    <p className="mt-1 text-[11px] text-ele-body opacity-70">
                      Show itemises the detected London zone charge as a labelled pass-through (default). Fold absorbs it into the maintenance price. Waive does not charge it. The amount is always the locked postcode detection.
                    </p>
                  </div>
                  {/* Manual price override (+ its tier/value/reason inline when on). */}
                  <div className="min-w-[15rem] flex-1">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={admin.overrideOn} onChange={(e) => setAdminField("overrideOn", e.target.checked)} />
                      Manual price override (calculator bypassed)
                    </label>
                    {/* Session APK: refused on the equipment-only shape, said here where it is fixed. */}
                    {equipmentOnlyOverride && (
                      <p role="alert" className="mt-1 text-xs text-ele-strong">
                        {EQUIPMENT_ONLY_OVERRIDE_MESSAGE}
                      </p>
                    )}
                    {admin.overrideOn && (
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <SelectField label="Tier" name="overrideTier" value={admin.overrideTier} onChange={(e) => setAdminField("overrideTier", e.target.value)} options={TIERS.map((t) => ({ value: t.value, label: t.label }))} />
                        <NumberField label="Forced annual (£ ex VAT)" name="overrideValue" value={admin.overrideValue} min={0} step={1} onChange={(e) => setAdminField("overrideValue", e.target.value)} />
                        {/* Session AAQ: a manual override bypasses the contract floor, so the office must
                            record why. Required on save; the server also rejects a save without it. */}
                        <div className="sm:col-span-2">
                          <Field
                            label="Reason for override"
                            name="overrideReason"
                            value={admin.overrideReason}
                            onChange={(e) => setAdminField("overrideReason", e.target.value)}
                            errors={fieldErrors.override}
                            required
                            placeholder="Why the standard price is being overridden (recorded on the quote)"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Soft floor (Session ANR, C59): shown ONLY once the live preview reports this
                      quote pricing below the contract floor. The floor still applies by default
                      (unchanged from before); confirming here is what lets the lower, natural price
                      stand instead, and it must be given a reason before it can be saved. */}
                  {total?.floorBreach && (
                    <div className="min-w-[15rem] flex-1 rounded-ele border border-amber-500 bg-amber-500/10 p-3">
                      <p className="text-sm text-amber-400">
                        This quote prices at {gbp(total.floorBreach.rawAnnual)}/yr, {gbp(total.floorBreach.shortfall)}/yr below the{" "}
                        {gbp(total.floorBreach.floorAnnual)}/yr contract floor for this frequency. The floor price will apply unless you
                        confirm below and give a reason.
                      </p>
                      <label className="mt-2 flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={admin.floorOverrideOn}
                          onChange={(e) => setAdminField("floorOverrideOn", e.target.checked)}
                        />
                        Confirm pricing below the contract floor
                      </label>
                      {/* The reason Field below carries its own error once the checkbox is on; while
                          it is off (the checkbox itself is what a "confirm first" error points at)
                          there is nowhere else for that message to render. */}
                      {!admin.floorOverrideOn &&
                        fieldErrors.floorOverride?.map((msg) => (
                          <p key={msg} role="alert" className="mt-1 text-xs text-ele-strong">
                            {msg}
                          </p>
                        ))}
                      {admin.floorOverrideOn && (
                        <div className="mt-2">
                          <Field
                            label="Reason for pricing below the floor"
                            name="floorOverrideReason"
                            value={admin.floorOverrideReason}
                            onChange={(e) => setAdminField("floorOverrideReason", e.target.value)}
                            errors={fieldErrors.floorOverride}
                            required
                            placeholder="Why this quote is priced below the standard minimum (recorded on the quote)"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {/* Promotional introductory rate (+ its pct/months/reason inline when on). */}
                  <div className="min-w-[15rem] flex-1">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={admin.promoOn} onChange={(e) => setAdminField("promoOn", e.target.checked)} />
                      Promotional introductory rate (presentation only, the annual value is unchanged)
                    </label>
                    {admin.promoOn && (
                      <div className="mt-2 grid gap-2 sm:grid-cols-3">
                        <NumberField label="Discount %" name="promoPct" value={admin.promoPct} min={1} max={100} step={1} onChange={(e) => setAdminField("promoPct", e.target.value)} />
                        <NumberField label="Months" name="promoMonths" value={admin.promoMonths} min={1} max={60} step={1} onChange={(e) => setAdminField("promoMonths", e.target.value)} />
                        <TextField label="Reason" name="promoReason" value={admin.promoReason} onChange={(e) => setAdminField("promoReason", e.target.value)} />
                      </div>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
          </div>

          {/* ---- The pricing rail (right). ACP split scroll, spec 3.1/3.3: on lg+ the
              rail is its own fixed ~400px column with its OWN scrollbar (never sticky --
              it is simply not inside the form's scroll container), styled as the dark
              signature panel. Below lg it remains the stacked card it has always been,
              rendered after the form sections exactly as before. */}
          <aside className="mx-6 mb-6 mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 lg:m-0 lg:w-[400px] lg:shrink-0 lg:overflow-y-auto lg:rounded-none lg:border-0 lg:bg-ele-inset lg:p-5">
              <h3 className="font-heading text-lg text-ele-blue">Pricing (per year)</h3>
              <p className="mt-1 text-xs text-ele-body opacity-70">
                {equipmentOnly
                  ? "Care equipment only: one clear price, no tiers. It updates as you configure."
                  : "Pick the tier to recommend. Prices update as you configure."}
              </p>

              <div className="mt-3 space-y-1.5">
                {previewError ? (
                  <p role="alert" className="text-sm text-ele-strong">Could not price that. Adjust the options and try again.</p>
                ) : !total ? (
                  <p className="text-sm text-ele-body">{previewing ? "Pricing..." : "Configure a unit to see the price."}</p>
                ) : equipmentOnly ? null : (
                  TIERS.map((t) => {
                    const tierPrice = total.tiers[t.value];
                    // Mixed quote (ACP + ACS): the card shows the tier's MAINTENANCE PLAN
                    // figure -- lift maintenance plus any tier-covered additional items
                    // (which genuinely differ per tier). The flat extras (equipment +
                    // tiers-off additional items) are their own separated blocks below,
                    // the same on every plan. Lift-only (with or without covered items):
                    // unchanged all-in card. With equipment only, maintenanceAnnual equals
                    // the ACP liftAnnual figure exactly.
                    const hasSeparatedExtras = hasEquipment || hasFlatAdditional;
                    const cardAnnual = hasSeparatedExtras ? tierPrice.maintenanceAnnual ?? tierPrice.annual : tierPrice.annual;
                    const cardSuffix = hasSeparatedExtras ? (hasFlatAdditional ? " (plan)" : " (lifts)") : "";
                    const isSelected = t.value === selectedTier;
                    return (
                      <button
                        type="button"
                        key={t.value}
                        onClick={() => setSelectedTier(t.value)}
                        aria-pressed={isSelected}
                        // Item 10 (Session ACB): the recommended (selected) tier is unmistakable at a
                        // glance -- a 2px brand-coloured border, a darker tint and a solid RECOMMENDED
                        // pill. Border width stays 2px on both states so selecting never shifts the layout.
                        className={`relative w-full rounded-ele border-2 px-3 py-2 text-left transition ${
                          isSelected ? `${t.border} bg-ele-inset-strong shadow-lg shadow-[var(--ele-shadow)]` : "border-ele-purple-accent hover:border-ele-blue"
                        }`}
                      >
                        <div className="flex items-baseline justify-between">
                          <span className={`font-heading text-sm uppercase tracking-wide ${isSelected ? t.text : "text-ele-strong"}`}>
                            {t.label}
                            {isSelected && (
                              <span className={`ml-2 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${t.badge}`}>
                                Recommended
                              </span>
                            )}
                          </span>
                          <span className="font-heading text-lg text-ele-strong">{gbp(cardAnnual)}<span className="ml-1 text-xs text-ele-body opacity-70">/yr ex VAT{cardSuffix}</span></span>
                        </div>
                        <div className="mt-0.5 flex items-center justify-between text-xs text-ele-body">
                          <span>{t.blurb}</span>
                          <span>
                            {total.promo && tierPrice.promoMonthly != null ? (
                              <>
                                {gbp(tierPrice.promoMonthly)}/mo<span className="opacity-60"> (first {total.promo.months} mo)</span>
                              </>
                            ) : (
                              <>{gbp(tierPrice.monthlyIncVat)}/mo inc VAT</>
                            )}
                          </span>
                        </div>
                        {uniformFreq && (
                          <div className="mt-1 flex items-center justify-end text-[11px] text-ele-body opacity-70">
                            <span>{gbp(perVisit(cardAnnual, total.visitsYr * total.numLifts))}/visit</span>
                          </div>
                        )}
                        <div className="mt-1 text-[11px] text-ele-body opacity-70">
                          Call-outs: 1st hr {gbp(tierPrice.call1)} · +1hr {gbp(tierPrice.callAdd)} · out of hours {gbp(tierPrice.callOOH)}
                        </div>
                        {/* Session AMP (Director decision, 25 August 2026). The line above is the
                            TIER-DISCOUNTED call-out card, so on Complete it reads "+1hr £93.75" and
                            an office user quoting an hourly rate off this card would undercharge a
                            repair by a third. Repair labour is £125.00 an hour at every tier, from
                            the first hour, so the SAME figure prints on all three cards: identical
                            across the row is what "flat, whatever the tier" looks like at a glance.
                            Read from the shared REPAIR_HOUR_DISPLAY rather than typed per card. */}
                        <div className="mt-1 text-[11px] text-ele-body opacity-70">
                          Repair labour: {REPAIR_HOUR_DISPLAY}/hr weekdays before {REPAIR_EVENING_START_HOUR}:00, {REPAIR_HOUR_OOH_DISPLAY}/hr other times, any tier
                        </div>
                        {(hasEquipment || hasFlatAdditional) && (
                          <div className="mt-1 text-[11px] text-ele-body">
                            Site total with {hasFlatAdditional ? "extras" : "equipment"}: <span className="text-ele-strong">{gbp(tierPrice.annual)}/yr</span>
                          </div>
                        )}
                        {/* Session ACV: the per-lift OOH inclusion note never shows on a
                            lift-less covered quote (Session ADA, Director decision 12 July
                            2026: gates-only Complete has no free included call-outs; the ESA
                            omits the line on that shape too). */}
                        {t.valueNote && !(units.length === 0 && hasCovered) && (
                          <div className={`mt-1 text-[11px] ${isSelected ? t.text : "text-ele-body"} opacity-90`}>{t.valueNote}</div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>

              {/* ---- CARE EQUIPMENT (ACP, spec 2.4/3.3): the separated equipment block.
                  Blue eyebrow (technical/reference data); the same figure on every tier,
                  shown once, so it never reads as carrying a tier's parts allowance. On an
                  equipment-only quote this IS the price block (green total, the single
                  most important number on the rail). ---- */}
              {hasEquipment && total && !previewError && (
                <div className={equipmentOnly ? "mt-3" : "mt-4 border-t border-ele-purple-accent pt-3"}>
                  <p className="font-menu text-[10px] uppercase tracking-widest text-ele-blue">Care equipment</p>
                  <div className="mt-1.5 space-y-1 text-xs text-ele-body">
                    {equipmentRows.map((row, i) => (
                      <div key={i} className="flex items-baseline justify-between gap-2">
                        <span className="min-w-0 truncate">
                          {row.description}
                          {row.qty > 1 ? ` x ${row.qty}` : ""}
                          {row.visitsYr != null && EQUIPMENT_FREQ_LABEL[row.visitsYr] ? (
                            <span className="opacity-70"> · {EQUIPMENT_FREQ_LABEL[row.visitsYr]}</span>
                          ) : null}
                        </span>
                        <span className="shrink-0 text-ele-strong">{gbp(row.annual)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex items-baseline justify-between border-t border-ele-purple-accent pt-2">
                    <span className="font-heading text-sm uppercase tracking-wide text-ele-strong">Equipment total</span>
                    <span className={`font-heading ${equipmentOnly ? "text-2xl text-ele-green" : "text-lg text-ele-strong"}`}>
                      {gbp(total.careHomeTotal ?? 0)}
                      <span className="ml-1 text-xs text-ele-body opacity-70">/yr ex VAT</span>
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-ele-body opacity-70">
                    {equipmentOnly
                      ? `Maintenance only: one clear price, no tiers. Billed ${frequencyLabel(total.billingFrequency).toLowerCase()}.`
                      : units.length === 0
                        ? "Maintenance only, no tiers: the same figure on every plan, separate from the item-built plans above."
                        : "Maintenance only, no tiers: the same figure on every tier, separate from the lift plans above."}
                  </p>
                  {/* Session APK: the rail says why this quote cannot be saved (or autosaved) as it stands. */}
                  {equipmentOnlyOverride && (
                    <p className="mt-1 text-[11px] text-ele-strong">
                      The manual price override is on. This quote cannot be saved until it is turned off (see Pricing tools).
                    </p>
                  )}
                  {/* Session ACS item A: the office workings note when the single-item site
                      minimum lifted the lone item's charge (server-flagged; the customer
                      documents show the resulting price only). */}
                  {total.equipment?.singleItemMinimum && (
                    <p className="mt-1 text-[11px] text-ele-blue">
                      Single-item site minimum | {gbp(total.equipment.singleItemMinimum.perVisit)}/visit applied
                    </p>
                  )}
                </div>
              )}

              {/* ---- ADDITIONAL ITEMS, flat / maintenance-only (Session ACS): the separated
                  extras treatment, mirroring the equipment block -- tiers-off rows are the
                  same figure on every plan and carry no tier benefits. Tier-covered rows are
                  NOT here: they live inside the tier cards above, itemised in the breakdown. */}
              {hasFlatAdditional && total && !previewError && (
                <div className="mt-4 border-t border-ele-purple-accent pt-3">
                  <p className="font-menu text-[10px] uppercase tracking-widest text-ele-blue">Additional items</p>
                  <div className="mt-1.5 space-y-1 text-xs text-ele-body">
                    {flatAdditional.map((it, i) => (
                      <div key={i} className="flex items-baseline justify-between gap-2">
                        <span className="min-w-0 truncate">
                          {it.description}
                          {(it.qty ?? 1) > 1 ? ` x ${it.qty}` : ""}
                        </span>
                        <span className="shrink-0 text-ele-strong">{gbp(it.price * (it.qty ?? 1))}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex items-baseline justify-between border-t border-ele-purple-accent pt-2">
                    <span className="font-heading text-sm uppercase tracking-wide text-ele-strong">Additional items total</span>
                    <span className="font-heading text-lg text-ele-strong">
                      {gbp(total.additionalItemsTotal ?? 0)}
                      <span className="ml-1 text-xs text-ele-body opacity-70">/yr ex VAT</span>
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-ele-body opacity-70">
                    Maintenance only: the same figure on every plan, with no parts allowance or tier benefits.
                  </p>
                </div>
              )}

              {total && !previewError && (
                <div className="mt-3 space-y-1 border-t border-ele-purple-accent pt-2 text-xs text-ele-body">
                  <p>
                    Billing period: <span className="text-ele-strong">{frequencyLabel(total.billingFrequency)}</span>
                    {!uniformFreq ? " (the shortest of the per-unit frequencies)" : ""}
                    {previewing ? " (updating...)" : ""}
                  </p>
                  {/* Session AAQ: the surcharge note reads the RAW detection so it is accurate whether the
                      office chose to show, fold or waive the charge. */}
                  {total.detectedSurcharges && total.detectedSurcharges.annualTotal > 0 && (() => {
                    const d = total.detectedSurcharges!;
                    const perVisit = (d.ccz ? d.cczPerVisit : 0) + (d.westminsterParking ? d.parkingPerVisit : 0);
                    const zones = [d.ccz ? "Congestion Charge Zone" : null, d.westminsterParking ? "Westminster/K&C parking" : null]
                      .filter(Boolean)
                      .join(" + ");
                    const mode = total.surchargePresentation ?? "show";
                    return (
                      <p className={mode === "waive" ? "text-amber-400" : "text-ele-blue"}>
                        {mode === "show" && `Postcode surcharge: ${gbp(perVisit)}/visit applied (${zones}).`}
                        {mode === "fold" && `Postcode surcharge ${gbp(perVisit)}/visit (${zones}) folded into the maintenance price (not itemised to the customer).`}
                        {mode === "waive" && `Postcode surcharge ${gbp(perVisit)}/visit (${zones}) waived (not charged).`}
                      </p>
                    );
                  })()}
                  {total.extrasTotal > 0 && <p>Additional items and equipment: {gbp(total.extrasTotal)}/yr (added to every tier).</p>}
                  {hasCovered && coveredBlock && (
                    <p className="text-ele-blue">
                      Tier-covered additional items: Core {gbp(coveredBlock.totals.core)} · Care {gbp(coveredBlock.totals.care)} · Complete{" "}
                      {gbp(coveredBlock.totals.complete)}/yr, inside each plan's price (coverage follows the chosen tier).
                    </p>
                  )}
                  {discountNote && <p className="text-ele-blue">{discountNote}</p>}
                  {/* Session AHC: where the portfolio count came from. The band moves money, so the
                      office can check the number rather than take it on trust. */}
                  {portfolioSplit && <p className="opacity-60">{portfolioSplit}</p>}
                  {total.promo && <p className="text-ele-green">Promo: {total.tiers[shownTier].promoMonthly != null ? `${gbp(total.tiers[shownTier].promoMonthly!)}/mo for the first ${total.promo.months} months (${total.promo.pct}% off). Standard rate thereafter.` : ""}</p>}
                  {total.override && (
                    <p className="text-amber-400">
                      Manual price (calculator bypassed) on {TIERS.find((t) => t.value === total.override!.tier)?.label}.
                      {total.override.reason ? ` Reason: ${total.override.reason}` : " A reason is required to save."}
                    </p>
                  )}
                  {/* Soft floor (Session ANR, C59): once confirmed, restate the decision compactly next
                      to the other governance notes above (mirrors the manual-override note). */}
                  {total.floorBreach && total.floorOverride && (
                    <p className="text-amber-400">
                      Priced {gbp(total.floorBreach.shortfall)}/yr below the {gbp(total.floorBreach.floorAnnual)}/yr contract floor.
                      {total.floorOverride.reason ? ` Reason: ${total.floorOverride.reason}` : " A reason is required to save."}
                    </p>
                  )}
                  {band && (
                    <p className={band.tone}>
                      Internal: {band.label} (cost floor {gbp(total.costFloor)}/yr, not shown to the customer).
                    </p>
                  )}
                </div>
              )}

              {total && !previewError && ((total.additionalItems?.length ?? 0) > 0 || (total.careHomeItems?.length ?? 0) > 0 || (total.surcharges?.annualTotal ?? 0) > 0) && (
                <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-bg/40 p-3 text-xs text-ele-body">
                  <h4 className="mb-2 font-heading text-xs uppercase tracking-wide text-ele-blue">
                    Price breakdown ({TIERS.find((t) => t.value === shownTier)?.label})
                  </h4>
                  {/* Session AAE: the maintenance row EXCLUDES the itemised surcharge lines,
                      exactly as the customer accept page and quote PDF show it, so the office
                      sees the same split the customer sees and the rows sum to the site total.
                      Session ACV: on a lift-less covered quote there is no lift maintenance at
                      all (the covered items ARE the plan), so the zero row is dropped -- but a
                      NON-ZERO residual (a manual override, or fractional extras rounding)
                      still renders so the breakdown always sums to the site total. */}
                  {(() => {
                    const residual =
                      (total.tiers[shownTier].maintenanceAnnual ?? total.tiers[shownTier].annual) -
                      (total.surcharges?.annualTotal ?? 0) -
                      (coveredBlock?.totals[shownTier] ?? 0);
                    const liftless = units.length === 0 && hasCovered;
                    if (liftless && residual === 0) return null;
                    return (
                      <div className="flex items-center justify-between">
                        <span>{liftless ? "Price adjustment" : "Lift maintenance"}</span>
                        <span className="text-ele-strong">{gbp(residual)}</span>
                      </div>
                    );
                  })()}
                  {/* Session ACS: the tier-covered items itemised at the SELECTED tier's scaled
                      figure -- they sit inside the plan (maintenance) amount, never the flat extras. */}
                  {hasCovered &&
                    coveredBlock!.rows.map((row, i) => (
                      <div key={`cov-${i}`} className="mt-1 flex items-center justify-between">
                        <span>
                          {row.description}
                          {row.qty > 1 ? ` x ${row.qty}` : ""} <span className="text-ele-blue">(tier coverage)</span>
                        </span>
                        <span className="text-ele-strong">{gbp(row.annual[shownTier])}</span>
                      </div>
                    ))}
                  {(total.surcharges?.cczAnnual ?? 0) > 0 && (
                    <div className="mt-1 flex items-center justify-between">
                      <span>{"Congestion Charge Zone surcharge"} ({gbp(total.surcharges!.cczPerVisit)}/visit)</span>
                      <span className="text-ele-strong">{gbp(total.surcharges!.cczAnnual)}</span>
                    </div>
                  )}
                  {(total.surcharges?.parkingAnnual ?? 0) > 0 && (
                    <div className="mt-1 flex items-center justify-between">
                      <span>{"Westminster/K&C parking surcharge"} ({gbp(total.surcharges!.parkingPerVisit)}/visit)</span>
                      <span className="text-ele-strong">{gbp(total.surcharges!.parkingAnnual)}</span>
                    </div>
                  )}
                  {hasFlatAdditional && (
                    <div className="mt-1">
                      <div className="flex items-center justify-between">
                        <span>Additional items (maintenance only)</span>
                        <span className="text-ele-strong">{gbp(total.additionalItemsTotal ?? 0)}</span>
                      </div>
                      <ul className="mt-0.5 space-y-0.5 pl-3 opacity-70">
                        {flatAdditional.map((it, i) => (
                          <li key={`add-${i}`}>{it.description} x {it.qty ?? 1} at {gbp(it.price)}/yr = {gbp(it.price * (it.qty ?? 1))}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(total.careHomeItems?.length ?? 0) > 0 && (
                    <div className="mt-1">
                      <div className="flex items-center justify-between">
                        <span>Care-home equipment</span>
                        <span className="text-ele-strong">{gbp(total.careHomeTotal ?? 0)}</span>
                      </div>
                      <ul className="mt-0.5 space-y-0.5 pl-3 opacity-70">
                        {total.careHomeItems!.map((it, i) => (
                          <li key={`care-${i}`}>{it.description} x {it.qty ?? 1} at {gbp(it.price)}/yr = {gbp(it.price * (it.qty ?? 1))}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between border-t border-ele-purple-accent pt-2">
                    <span className="font-heading uppercase tracking-wide text-ele-strong">Site total</span>
                    <span className="font-heading text-ele-green">{gbp(total.tiers[shownTier].annual)}/yr</span>
                  </div>
                  <p className="mt-1 opacity-60">Extras are flat and never multi-unit discounted; tier-covered items are never discounted either.</p>
                </div>
              )}

              <button type="button" onClick={() => setShowWorkings((s) => !s)} className="mt-3 text-xs text-ele-blue underline">
                {showWorkings ? "Hide workings" : "Show workings"}
              </button>
              {showWorkings && total && !previewError && (
                <div className="mt-2 space-y-1 rounded-ele border border-ele-purple-accent bg-ele-bg/40 p-3 text-[11px] text-ele-body">
                  {units.map((u, i) => {
                    const up = unitPrices[u.id];
                    if (!up || up === "error") return <p key={u.id}>Unit {i + 1}: price unavailable</p>;
                    return (
                      <p key={u.id}>
                        Unit {i + 1}: Core {gbp(up.tiers.core.annual)} · Care {gbp(up.tiers.care.annual)} · Complete {gbp(up.tiers.complete.annual)}
                      </p>
                    );
                  })}
                  <p className="border-t border-ele-purple-accent pt-1">Multi-unit discount: {Math.round(total.multiDiscount * 100)}%</p>
                  <p>Surcharge: {gbp(total.surchargePerVisit)}/visit x {total.visitsYr} visits/yr</p>
                  <p>Extras (not discounted): {gbp(total.extrasTotal)}/yr</p>
                  {hasCovered && coveredBlock && (
                    <p>
                      Tier-covered items (not discounted): Core {gbp(coveredBlock.totals.core)} · Care {gbp(coveredBlock.totals.care)} · Complete{" "}
                      {gbp(coveredBlock.totals.complete)}
                    </p>
                  )}
                  {total.equipment?.singleItemMinimum && (
                    <p>Single-item site minimum | {gbp(total.equipment.singleItemMinimum.perVisit)}/visit applied</p>
                  )}
                  <p>Internal cost floor: {gbp(total.costFloor)}/yr</p>
                  {/* Session AAT Loop 5: office-only margin (price minus estimated labour + parts).
                      Advisory; never shown to the customer, never stored on the quote. */}
                  {margins && labourCostPerHour != null && (
                    <div className="border-t border-ele-purple-accent pt-1">
                      <p className="text-ele-strong">Internal margin (office only) at {gbp(labourCostPerHour)}/hr labour, {total.serviceHoursYr ?? 0} hrs/yr:</p>
                      {(["core", "care", "complete"] as const).map((t) => (
                        <p key={t}>
                          {TIERS.find((x) => x.value === t)?.label}: cost {gbp(margins[t].costAnnual)} · margin{" "}
                          <span className={margins[t].marginPounds < 0 ? "text-ele-danger" : "text-ele-green"}>
                            {gbp(margins[t].marginPounds)} ({margins[t].marginPercent}%)
                          </span>
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="border-t border-ele-purple-accent pt-1 text-ele-strong">Site total ({TIERS.find((t) => t.value === shownTier)?.label}): {gbp(total.tiers[shownTier].annual)}/yr ex VAT</p>
                </div>
              )}
            </aside>
        </div>

        {/* ---- The always-visible action bar (fixed height, never scrolled away) ---- */}
        <div className="shrink-0 border-t border-ele-purple-accent px-6 py-3">
          {formError && (
            <p role="alert" className="mb-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {formError}
            </p>
          )}
          {fieldErrors.lifts && (
            <p role="alert" className="mb-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              Check the unit configuration above and try again.
            </p>
          )}
          {/* Session APK: a save refused for a manual override on an equipment-only quote. Shown only while the
              override is still on, so it can never outlive what it reports. */}
          {equipmentOnlyOverride && fieldErrors.equipmentOnlyOverride && (
            <p role="alert" className="mb-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {EQUIPMENT_ONLY_OVERRIDE_NOT_SAVED}
            </p>
          )}
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-end gap-3 lg:max-w-none">
            <span className="mr-auto text-xs text-ele-body opacity-70">
              {detailsOk && !priceFresh ? (
                <span className="text-ele-blue">{previewError ? "Adjust the unit. That price could not be calculated." : "Waiting for the live price to settle..."}</span>
              ) : equipmentOnly ? (
                <>
                  Equipment-only quote{priceFresh && total ? ` at ${gbp(total.tiers.core.annual)}/yr ex VAT` : ""}. Maintenance only, no tiers.
                </>
              ) : (
                <>
                  Recommending <span className="text-ele-green">{TIERS.find((t) => t.value === selectedTier)?.label}</span>
                  {priceFresh && total ? ` at ${gbp(total.tiers[selectedTier].annual)}/yr ex VAT` : ""}.
                </>
              )}
            </span>
            {/* Always-mounted live region (a conditionally-mounted role=status is not reliably
                announced), quietly naming the autosaved draft. Session AAV. */}
            <span role="status" aria-live="polite" className="text-xs text-ele-body opacity-60">
              {autosaveStatus
                ? `Autosaved as draft ${autosaveStatus.ref} at ${formatTime(autosaveStatus.at)}.`
                : ""}
            </span>
            <button
              type="button"
              onClick={() => submit("draft")}
              disabled={!canSaveDraft}
              className="rounded-ele border border-ele-blue px-5 py-2.5 font-heading tracking-wide text-ele-blue disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save draft"}
            </button>
            <Button
              type="submit"
              disabled={!canSend}
              title={!details.customerEmail.trim() ? "Add a customer email to send" : undefined}
            >
              {saving ? "Sending..." : "Send quote"}
            </Button>
          </div>
        </div>
      </form>

      {/* Session AAT Loop 4: save the current configuration as a template, or load one. */}
      {templatesOpen && (
        <QuoteTemplatesModal
          currentConfig={templateConfig}
          onApply={applyTemplate}
          onClose={() => setTemplatesOpen(false)}
        />
      )}
    </div>
  );
}

function makePresetRow(preset: LinePreset): LineRow {
  return {
    id: rowSeq++,
    description: preset.description,
    price: String(preset.pricePerYear),
    qty: "1",
    // ACP: an equipment preset carries its LOLER minimum; the row starts serviced AT
    // that minimum (the catalogue price buys exactly the minimum visits).
    ...(preset.minVisitsYr != null ? { minVisits: String(preset.minVisitsYr), chosenVisits: String(preset.minVisitsYr) } : {}),
  };
}

function LineItemSection({
  title,
  hint,
  rows,
  setRows,
  presets,
  presetLabel,
  equipment = false,
  tierToggle = false,
  liftless = false,
  coveredPriceFor,
  singleItemMinimum = null,
}: {
  title: string;
  hint: string;
  rows: LineRow[];
  setRows: Dispatch<SetStateAction<LineRow[]>>;
  presets: LinePreset[];
  presetLabel: string;
  // ACP care equipment model: equipment rows carry a LOLER minimum + a serviced
  // cadence (never below the minimum) and show their computed tapered annual.
  equipment?: boolean;
  // Session ACS (additional items only): render the per-row Tier coverage switch.
  // Session ACV: the switch ALWAYS works -- a lift-less quote with covered items is a
  // real tiered contract (the items form the plan), so the ACS disabled state is gone.
  tierToggle?: boolean;
  // Session ACV (additional items only): true while the quote has NO lifts. A covered
  // row then shows its service-frequency select (yearly / six-monthly / quarterly) --
  // scheduling only, never a price input; hidden on a mixed quote (items ride the
  // lift visits there) with the row's value retained.
  liftless?: boolean;
  // The covered row's server-priced per-tier annuals (Core | Care | Complete),
  // shown inline under a tiers-on row. Null while the live preview is settling.
  coveredPriceFor?: (row: LineRow) => { core: number; care: number; complete: number } | null;
  // Session ACS item A (equipment section only): the single-item site minimum when it
  // applies -- the lone row's displayed annual lifts to the floored figure with the
  // office workings note.
  singleItemMinimum?: { perVisit: number; annual: number } | null;
}) {
  const update = (id: number, patch: Partial<LineRow>) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addPreset = (e: ChangeEvent<HTMLSelectElement>) => {
    const idx = Number.parseInt(e.target.value, 10);
    const preset = presets[idx];
    if (preset) setRows((rs) => [...rs, makePresetRow(preset)]);
    // The <select> is controlled with a constant value="", so it returns to its
    // placeholder on the next render -- no imperative reset needed.
  };
  // Keyboard-first (Session AAV, Loop 2): Enter inside a row adds the next custom row and
  // moves focus to its description, so a list of items can be keyed without the mouse. The
  // new row's input takes focus via autoFocus on mount. An equipment section's custom row
  // carries the safe six-monthly minimum default (makeEquipmentRow).
  const [focusRowId, setFocusRowId] = useState<number | null>(null);
  const onRowKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Plain Enter only: Ctrl/Cmd+Enter is the send shortcut (handled at the form level) and
    // must not also append a stray row here.
    if (e.key !== "Enter" || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    const row = equipment ? makeEquipmentRow() : makeRow();
    setFocusRowId(row.id);
    setRows((rs) => [...rs, row]);
  };
  // The office changed a row's LOLER minimum (declaring the custom item people-lifting or
  // fixed-height): keep the serviced cadence valid by lifting it to at least the new minimum.
  const changeMin = (r: LineRow, value: string) => {
    const min = Number.parseInt(value, 10);
    const chosen = Number.parseInt(r.chosenVisits ?? "", 10);
    update(r.id, { minVisits: value, chosenVisits: Number.isFinite(chosen) && chosen >= min ? r.chosenVisits : value });
  };
  return (
    <div className="mt-5 border-t border-ele-purple-accent pt-4">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-sm uppercase tracking-wide text-ele-strong">{title}</h3>
        <div className="flex items-center gap-3">
          <select
            aria-label={presetLabel}
            value=""
            onChange={addPreset}
            className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs text-ele-body outline-none focus:border-ele-green"
          >
            <option value="">{presetLabel}...</option>
            {presets.map((p, i) => (
              <option key={p.description} value={i}>
                {p.description} ({`£${p.pricePerYear}`}/yr)
              </option>
            ))}
          </select>
          <button type="button" onClick={() => setRows((rs) => [...rs, equipment ? makeEquipmentRow() : makeRow()])} className="text-xs text-ele-green underline">+ Add custom</button>
        </div>
      </div>
      <p className="mb-2 text-xs text-ele-body opacity-70">{hint}</p>
      {rows.length === 0 ? (
        <p className="text-xs text-ele-body opacity-50">None.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const rowMin = Number.parseInt(r.minVisits ?? "", 10);
            const hasFreq = equipment && (rowMin === 1 || rowMin === 2);
            const rowAnnual = hasFreq
              ? equipmentRowAnnual({
                  price: Number.parseFloat(r.price),
                  qty: Number.parseInt(r.qty, 10) || 1,
                  minVisitsYr: rowMin,
                  chosenVisitsYr: Number.parseInt(r.chosenVisits ?? "", 10) || rowMin,
                })
              : null;
            const coveredPrices = tierToggle && r.tiersOn === true && coveredPriceFor ? coveredPriceFor(r) : null;
            return (
              <div key={r.id}>
                {/* Session AIB, mobile audit M2. This was a bare `grid grid-cols-12` with no
                    responsive prefix, the only grid in this file without one, while every other
                    grid here is mobile-first (`sm:grid-cols-2`, `sm:grid-cols-3`, no base column
                    count). At 288px each twelfth is about 16.7px, so Qty at col-span-1 was a ~41px
                    input with ~25px of typing space after its px-2, and Remove was a ~24px text
                    link. Session ACW's min-height rule fixes the HEIGHT of these fields; nothing
                    set their width.
                    It matters more than a niche tool: there is no /office/quotes/:id route, so this
                    page IS the office's only surface for writing or editing a quote.
                    Below sm the row is a two-column stack (description, selects and Remove full
                    width; price and quantity side by side, ~150px each); from sm up the twelfths
                    are exactly as they were, so the desktop row is byte-identical in layout. Every
                    variant still sums to 12 at sm and above. */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-12">
                  <input
                    className={`col-span-2 ${hasFreq ? "sm:col-span-4" : tierToggle ? "sm:col-span-4" : "sm:col-span-7"} rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green`}
                    placeholder="Description"
                    value={r.description}
                    autoFocus={r.id === focusRowId}
                    onKeyDown={onRowKeyDown}
                    onChange={(e) => update(r.id, { description: e.target.value })}
                  />
                  <input
                    className="col-span-1 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green sm:col-span-2"
                    placeholder="£/yr"
                    aria-label={hasFreq ? "Catalogue price per year at the minimum frequency" : r.tiersOn === true ? "Core price per year" : undefined}
                    title={hasFreq ? "The yearly price at the item's minimum service frequency" : r.tiersOn === true ? "The item's Core price; Care and Complete scale with the plan" : undefined}
                    inputMode="decimal"
                    value={r.price}
                    onKeyDown={onRowKeyDown}
                    onChange={(e) => update(r.id, { price: e.target.value })}
                  />
                  <input
                    className={`col-span-1 ${hasFreq || tierToggle ? "sm:col-span-1" : "sm:col-span-2"} rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green`}
                    placeholder="Qty"
                    inputMode="numeric"
                    value={r.qty}
                    onKeyDown={onRowKeyDown}
                    onChange={(e) => update(r.id, { qty: e.target.value })}
                  />
                  {/* Session ACS: the per-row tier-coverage switch (plain checkbox, no stripe).
                      ON: the item rides the plan (its price is the Core figure). Session ACV:
                      always enabled -- with no lifts the covered items form the plan. */}
                  {tierToggle && (
                    <label
                      className="col-span-2 flex min-h-11 items-center gap-1.5 self-center text-xs sm:col-span-4 sm:min-h-0"
                      title="ON: the item is priced into each plan and its parts/coverage follow the chosen tier. OFF: a flat maintenance-only line."
                    >
                      <input
                        type="checkbox"
                        checked={r.tiersOn === true}
                        onChange={(e) => update(r.id, { tiersOn: e.target.checked })}
                      />
                      Tier coverage
                    </label>
                  )}
                  {hasFreq && (
                    <>
                      <select
                        aria-label="Minimum service frequency (LOLER)"
                        title="The statutory minimum: six-monthly for equipment that lifts a person, yearly for fixed-height items"
                        value={String(rowMin)}
                        onChange={(e) => changeMin(r, e.target.value)}
                        className="col-span-2 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-xs text-ele-body outline-none focus:border-ele-green"
                      >
                        <option value="2">Min: Six-monthly (lifts people)</option>
                        <option value="1">Min: Yearly (fixed height)</option>
                      </select>
                      <select
                        aria-label="Serviced frequency"
                        value={String(Math.max(Number.parseInt(r.chosenVisits ?? "", 10) || rowMin, rowMin))}
                        onChange={(e) => update(r.id, { chosenVisits: e.target.value })}
                        className="col-span-2 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-xs text-ele-body outline-none focus:border-ele-green"
                      >
                        {equipmentFrequencyOptionsForMin(rowMin as EquipmentVisits).map((o) => (
                          <option key={o.value} value={String(o.value)}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  {/* Its own row below sm (a ~24px text link is not a tap target), right-aligned so
                      it does not read as another field, and floored at 44px like every other mobile
                      control in the office.
                      EVERY mobile utility here is `max-sm:`-prefixed ON PURPOSE, which is the one
                      place this file departs from its own mobile-first idiom. Two reasons, both
                      found by this session's adversarial review of an earlier draft that used
                      unprefixed utilities with `sm:` resets:
                      (1) DESKTOP MUST BE PROVABLY UNTOUCHED. A bare `inline-flex` blockifies the
                      button to display:flex, which makes the UA stylesheet's `text-align: center`
                      inert, so the label slid from the centre of its twelfth to its left edge at
                      every width from 640px up. Scoping to max-sm: leaves the sm+ button with
                      exactly its original classes, so the claim of desktop invariance is true
                      rather than nearly true.
                      (2) `justify-self-end` rather than a full-row `justify-end`: the earlier draft
                      made the whole ~343px row a hit target while only the ~48px word looked
                      tappable, so a tap meant to dismiss the keyboard could silently delete the
                      line the user was editing. This sizes the control to its content, which is
                      what MobileListCard does for card controls, with max-sm:px-3 widening the box
                      without underlining any more than the word. */}
                  <button
                    type="button"
                    onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}
                    className="col-span-2 justify-self-end text-xs text-ele-body underline hover:text-ele-danger max-sm:inline-flex max-sm:min-h-11 max-sm:items-center max-sm:px-3 sm:col-span-1 sm:justify-self-auto"
                  >
                    Remove
                  </button>
                </div>
                {rowAnnual != null && Number.isFinite(rowAnnual) && rowAnnual > 0 && (
                  <p className="mt-0.5 text-right text-[11px] text-ele-body opacity-70">
                    {EQUIPMENT_FREQ_LABEL[Math.max(Number.parseInt(r.chosenVisits ?? "", 10) || rowMin, rowMin)]}
                    {" servicing: "}
                    {/* Session ACS item A: with the single-item site minimum in force, the row's
                        charged figure is the floored one (the office workings note says why). */}
                    {singleItemMinimum != null && rowAnnual < singleItemMinimum.annual ? (
                      <>
                        <span className="text-ele-strong">{gbp(singleItemMinimum.annual)}/yr</span>
                        <span className="text-ele-blue"> · Single-item site minimum | {gbp(singleItemMinimum.perVisit)}/visit applied</span>
                      </>
                    ) : (
                      <span className="text-ele-strong">{gbp(rowAnnual)}/yr</span>
                    )}
                    {(Number.parseInt(r.chosenVisits ?? "", 10) || rowMin) > rowMin ? " (added visits at the reduced rate)" : ""}
                  </p>
                )}
                {/* Session ACS: a covered row's live per-tier prices (server-priced; the price
                    field is the Core figure). Shown while the toggle is ON. */}
                {tierToggle && r.tiersOn === true && (
                  <p className="mt-0.5 text-right text-[11px] text-ele-body opacity-70">
                    {coveredPrices ? (
                      <>
                        Tier coverage:{" "}
                        <span className="text-ele-green">Core {gbp(coveredPrices.core)}</span>
                        {" | "}
                        <span style={{ color: "#7d21c7" }}>Care {gbp(coveredPrices.care)}</span>
                        {" | "}
                        <span className="text-ele-blue">Complete {gbp(coveredPrices.complete)}</span>
                        <span> /yr, cover follows the chosen tier</span>
                      </>
                    ) : (
                      "Tier coverage: pricing..."
                    )}
                  </p>
                )}
                {/* Session ACV: on a LIFT-LESS quote each covered item carries its own service
                    frequency (yearly / six-monthly / quarterly). Scheduling only -- it never
                    changes the price; at activation the item becomes a scheduled asset at this
                    cadence. Hidden on a mixed quote (items ride the lift visits there). */}
                {tierToggle && r.tiersOn === true && liftless && (
                  <div className="mt-1 flex items-center justify-end gap-2">
                    <label className="text-[11px] text-ele-body opacity-70" htmlFor={`additional-service-freq-${r.id}`}>
                      Service frequency (sets the visit schedule, not the price)
                    </label>
                    <select
                      id={`additional-service-freq-${r.id}`}
                      value={(() => {
                        const sv = Number.parseInt(r.serviceVisits ?? "", 10);
                        return sv === 1 || sv === 2 || sv === 4 ? String(sv) : String(ADDITIONAL_SERVICE_DEFAULT_VISITS);
                      })()}
                      onChange={(e) => update(r.id, { serviceVisits: e.target.value })}
                      className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs text-ele-body outline-none focus:border-ele-green"
                    >
                      {additionalServiceFrequencyOptions().map((o) => (
                        <option key={o.value} value={String(o.value)}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Session ACR: people-lifting kit only -- an optional declared last exam. When
                    given, the equipment asset created at signing starts its examination clock at
                    this date + 6 months; when blank it starts "awaiting first examination record"
                    and surfaces on the office chase list. Never affects the price. */}
                {equipment && rowMin === 2 && (
                  <div className="mt-1 flex items-center justify-end gap-2">
                    <label className="text-[11px] text-ele-body opacity-70" htmlFor={`equip-last-exam-${r.id}`}>
                      Last LOLER exam, if known
                    </label>
                    <input
                      id={`equip-last-exam-${r.id}`}
                      type="date"
                      value={r.lastExam ?? ""}
                      onChange={(e) => update(r.id, { lastExam: e.target.value })}
                      className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs text-ele-body outline-none focus:border-ele-green"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  errors,
  required,
  type = "text",
  placeholder,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  errors?: string[];
  required?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm" htmlFor={name}>
        {label}
        {required && <span className="text-ele-green"> *</span>}
      </label>
      <input
        id={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
      />
      {errors?.map((msg) => (
        <p key={msg} role="alert" className="mt-1 text-xs text-ele-strong">
          {msg}
        </p>
      ))}
    </div>
  );
}

// Site-address input with free Nominatim autocomplete (Session AAP). A plain text field plus a
// suggestions dropdown; picking a suggestion fills the address (and its postcode when present). The
// input stays fully editable (manual entry always works); the dropdown only appears while focused.
// The suggestion buttons use onMouseDown + preventDefault so the pick fires before the input blurs.
function AddressAutocompleteField({
  value,
  onChange,
  onFocus,
  onBlur,
  onPick,
  suggestions,
  searching,
  open,
  errors,
}: {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onPick: (s: AddressSuggestion) => void;
  suggestions: AddressSuggestion[];
  searching: boolean;
  open: boolean;
  errors?: string[];
}) {
  const showDropdown = open && (searching || suggestions.length > 0);
  return (
    <div className="relative">
      <label className="mb-1 block text-sm" htmlFor="siteAddress">
        Site address
      </label>
      <input
        id="siteAddress"
        type="text"
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoComplete="off"
        placeholder="Start typing an address to search"
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
      />
      {showDropdown && (
        <ul data-vscroll className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] shadow-lg">
          {searching && suggestions.length === 0 ? (
            <li className="px-3 py-2 text-xs text-ele-body opacity-70">Searching addresses...</li>
          ) : (
            suggestions.map((s, i) => (
              <li key={`${s.label}-${i}`}>
                <button
                  type="button"
                  // preventDefault keeps focus on the input so the click registers before blur closes this.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onPick(s);
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-ele-body hover:bg-ele-inset hover:text-ele-strong"
                >
                  {s.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
      {errors?.map((msg) => (
        <p key={msg} role="alert" className="mt-1 text-xs text-ele-strong">
          {msg}
        </p>
      ))}
    </div>
  );
}

// Companies House company lookup for the client block (Session AAP). A search input (name or number)
// plus a results dropdown; picking a result fills the customer name, company number and registered
// office address. Degrades to a quiet "enter manually" note when the lookup is not configured. Uses
// onMouseDown + preventDefault so a pick fires before the input blurs.
function CompanyLookupField({
  query,
  onQueryChange,
  onFocus,
  onBlur,
  onPick,
  results,
  searching,
  configured,
  picking,
  open,
}: {
  query: string;
  onQueryChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onPick: (item: CompanySearchItem) => void;
  results: CompanySearchItem[];
  searching: boolean;
  configured: boolean | null;
  picking: boolean;
  open: boolean;
}) {
  const notConfigured = configured === false;
  const showDropdown = open && !notConfigured && (searching || results.length > 0);
  return (
    <div className="relative">
      <label className="mb-1 block text-sm" htmlFor="companyLookup">
        Find company (Companies House)
        <span className="text-ele-body opacity-60"> (optional)</span>
      </label>
      <input
        id="companyLookup"
        type="text"
        value={query}
        onChange={onQueryChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoComplete="off"
        disabled={picking}
        placeholder="Search a company name or number to auto-fill the client details"
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green disabled:opacity-60"
      />
      {picking && <p className="mt-1 text-xs text-ele-body opacity-70">Loading company details...</p>}
      {notConfigured && <p className="mt-1 text-xs text-ele-body opacity-70">Company lookup is not available. Enter the company details manually below.</p>}
      {showDropdown && (
        <ul data-vscroll className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] shadow-lg">
          {searching && results.length === 0 ? (
            <li className="px-3 py-2 text-xs text-ele-body opacity-70">Searching companies...</li>
          ) : (
            results.map((c) => (
              <li key={c.companyNumber}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onPick(c);
                  }}
                  className="block w-full px-3 py-2 text-left hover:bg-ele-inset"
                >
                  <span className="block text-sm text-ele-strong">{c.title}</span>
                  <span className="block text-xs text-ele-body opacity-70">
                    {c.companyNumber}
                    {c.status ? ` · ${c.status}` : ""}
                    {c.addressSnippet ? ` · ${c.addressSnippet}` : ""}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// A combobox over existing records (Session AAP, Loop 3): a text field that doubles as a search box,
// with a dropdown of matching existing customers/sites. Picking one pre-fills the quote and links it;
// typing keeps it a free-text entry for a brand-new customer/site. Used for the customer-name and
// site-name fields. onMouseDown + preventDefault so a pick fires before the input blurs.
interface ComboItem {
  id: string;
  primary: string;
  secondary: string | null;
}
function RecordComboField({
  label,
  name,
  value,
  onChange,
  onFocus,
  onBlur,
  onPick,
  items,
  searching,
  open,
  required,
  hint,
  errors,
  autoFocus,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onPick: (id: string) => void;
  items: ComboItem[];
  searching: boolean;
  open: boolean;
  required?: boolean;
  hint?: string;
  errors?: string[];
  autoFocus?: boolean;
}) {
  const showDropdown = open && (searching || items.length > 0);
  return (
    <div className="relative">
      <label className="mb-1 block text-sm" htmlFor={name}>
        {label}
        {required && <span className="text-ele-green"> *</span>}
      </label>
      <input
        id={name}
        type="text"
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        // Session ABC Loop 3: Enter picks the top match, so keyboard-only prefill works
        // (plain Enter never submits the form; the form-level guard also holds).
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.ctrlKey && !e.metaKey && items.length > 0) {
            e.preventDefault();
            onPick(items[0]!.id);
          }
        }}
        autoComplete="off"
        autoFocus={autoFocus}
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
      />
      {showDropdown && (
        <ul data-vscroll className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] shadow-lg">
          {searching && items.length === 0 ? (
            <li className="px-3 py-2 text-xs text-ele-body opacity-70">Searching records...</li>
          ) : (
            items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onPick(it.id);
                  }}
                  className="block w-full px-3 py-2 text-left hover:bg-ele-inset"
                >
                  <span className="block text-sm text-ele-strong">{it.primary}</span>
                  {it.secondary && <span className="block text-xs text-ele-body opacity-70">{it.secondary}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
      {hint && <p className="mt-1 text-xs text-ele-body opacity-60">{hint}</p>}
      {errors?.map((msg) => (
        <p key={msg} role="alert" className="mt-1 text-xs text-ele-strong">
          {msg}
        </p>
      ))}
    </div>
  );
}

function TextField({
  label,
  name,
  value,
  onChange,
  optional,
  placeholder,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  optional?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm" htmlFor={name}>
        {label}
        {optional && <span className="text-ele-body opacity-60"> (optional)</span>}
      </label>
      <input
        id={name}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
      />
    </div>
  );
}

function ManufacturerField({
  name,
  value,
  onChange,
}: {
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <DatalistField
      label="Manufacturer"
      name={name}
      value={value}
      suggestions={MANUFACTURER_SUGGESTIONS}
      placeholder="e.g. Otis"
      onChange={onChange}
    />
  );
}

// A text input with a datalist of suggestions: the office can pick a common value or
// type a custom one. Used for documentation-only fields (manufacturer, capacity).
function DatalistField({
  label,
  name,
  value,
  suggestions,
  placeholder,
  onChange,
}: {
  label: string;
  name: string;
  value: string;
  suggestions: readonly string[];
  placeholder?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}) {
  const listId = `${name}-list`;
  return (
    <div>
      <label className="mb-1 block text-sm" htmlFor={name}>
        {label}
        <span className="text-ele-body opacity-60"> (optional)</span>
      </label>
      <input
        id={name}
        type="text"
        value={value}
        onChange={onChange}
        list={listId}
        placeholder={placeholder}
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
      />
      <datalist id={listId}>
        {suggestions.map((m) => (
          <option key={m} value={m} />
        ))}
      </datalist>
    </div>
  );
}

// Year-of-manufacture combobox (Item 8, Session ACB): a text input that filters a dropdown of
// years (current year down to 1900) as the office types. Typing "199" narrows to 1990 to 1999;
// "2015" to just 2015. A dropdown pick, or a full valid year confirmed with Enter or Tab, sets it.
// A four-digit year outside 1900..current is rejected with inline validation. The bound value is a
// plain string, exactly as the old NumberField, so parseManufactureYear / ageBandFromYear downstream
// are unchanged. Exported for a focused unit test.
export function YearComboField({
  name,
  value,
  onChange,
}: {
  name: string;
  value: string;
  onChange: (year: string) => void;
}) {
  const currentYear = new Date().getFullYear();
  const minYear = 1900;
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const years = useMemo(
    () => Array.from({ length: currentYear - minYear + 1 }, (_, i) => String(currentYear - i)),
    [currentYear],
  );
  const trimmed = value.trim();
  const matches = trimmed === "" ? years : years.filter((y) => y.startsWith(trimmed));
  const isFourDigit = /^\d{4}$/.test(trimmed);
  const yearNum = Number(trimmed);
  const outOfRange = isFourDigit && (yearNum < minYear || yearNum > currentYear);

  // Close the dropdown when focus/click leaves the whole widget.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const commit = (year: string) => {
    onChange(year);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <label className="mb-1 block text-sm" htmlFor={name}>
        Year of manufacture
        <span className="text-ele-body opacity-60"> (optional)</span>
      </label>
      <input
        id={name}
        type="text"
        inputMode="numeric"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        value={value}
        placeholder="e.g. 2014"
        onChange={(e) => {
          // Digits only, at most four, so the field can only ever hold a year-shaped value.
          onChange(e.target.value.replace(/\D/g, "").slice(0, 4));
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            // Accept a fully-typed valid year, or an unambiguous single narrowing; otherwise just
            // dismiss. Never default to matches[0]: on an EMPTY or ambiguous prefix that would
            // silently fabricate a year (e.g. Enter on a blank field -> the current year), writing a
            // wrong "installed YYYY" into the ESA plant schedule (year of manufacture is optional).
            if (isFourDigit && !outOfRange) commit(trimmed);
            else if (trimmed !== "" && matches.length === 1) commit(matches[0]!);
            else setOpen(false);
          } else if (e.key === "Escape" || e.key === "Tab") {
            setOpen(false);
          }
        }}
        className={`w-full rounded-ele border bg-ele-field px-3 py-2 outline-none focus:border-ele-green ${
          outOfRange ? "border-ele-danger" : "border-ele-purple-accent"
        }`}
      />
      {open && matches.length > 0 && (
        <ul data-vscroll role="listbox" className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] shadow-lg">
          {matches.slice(0, 40).map((y) => (
            <li key={y}>
              <button
                type="button"
                role="option"
                aria-selected={y === trimmed}
                onMouseDown={(e) => e.preventDefault()} // keep focus so the click registers before blur
                onClick={() => commit(y)}
                className="block w-full px-3 py-1.5 text-left text-sm text-ele-body hover:bg-ele-purple-accent/30 hover:text-ele-strong"
              >
                {y}
              </button>
            </li>
          ))}
        </ul>
      )}
      {outOfRange && (
        <p role="alert" className="mt-1 text-xs text-ele-danger">
          Enter a year between {minYear} and {currentYear}.
        </p>
      )}
    </div>
  );
}

function NumberField({
  label,
  name,
  value,
  onChange,
  min,
  max,
  step,
  optional,
  placeholder,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  min?: number;
  max?: number;
  step?: number;
  optional?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm" htmlFor={name}>
        {label}
        {optional && <span className="text-ele-body opacity-60"> (optional)</span>}
      </label>
      <input
        id={name}
        type="number"
        inputMode="numeric"
        value={value}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        onChange={onChange}
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
      />
    </div>
  );
}

function SelectField({
  label,
  name,
  value,
  onChange,
  options,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  // `disabled`/`title` let a single option be greyed out with a tooltip explaining why
  // (used by the frequency select to gate annual on the restricted lift types).
  options: { value: string; label: string; disabled?: boolean; title?: string }[];
}) {
  return (
    <div>
      <label className="mb-1 block text-sm" htmlFor={name}>
        {label}
      </label>
      <select
        id={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled} title={o.title}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
